/**
 * Employee profile tab: cab passes with optional digital verification (QR).
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { translateUi } from './i18n/index';
import { createPortal, flushSync } from "react-dom";
import { QRCodeSVG } from "qrcode.react";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { Trash2, Plus, KeyRound, Printer, Info } from "./icons";
import { CabPassScaledCardPreview, printCabPassCreditCard } from "./CabPassScaledCardPreview";
import { DrivingCabPassCard } from "./DrivingCabPassCard";
import { cabPassCalendarToday, cabPassIsExpired, passSubtitleForColor } from "./drivingCabPassFormat";
import { GbrModalBackdrop } from "./GbrModalBackdrop";

export type CabPassesTabEmployee = {
  id: string;
  full_name: string;
  role?: string;
  depot?: string;
  email?: string;
  company_code?: string;
  company_name?: string;
  company_address?: string;
  /** Uploaded company logo — shown in the pass top-right box when set. */
  company_logo_url?: string;
  /** Assessing Manager (full name) — renewal allowed when matched to signed-in user's name. */
  manager?: string;
  /** Profile photo — shown on the printed-style pass when set. */
  image_url?: string;
};

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Scroll container for the employee profile (mobile + desktop shell). */
function employeeProfileScrollRoot(from?: HTMLElement | null): HTMLElement | null {
  if (from) {
    const near = from.closest(".rv-main-content") as HTMLElement | null;
    if (near) return near;
  }
  return document.querySelector<HTMLElement>(".rv-main-content");
}

/**
 * Compact / phone-tablet scroll behaviour — prefer live viewport signals so we still
 * run when `isMobileOrTablet` prop lags or a desktop-UA phone is used.
 */
function shouldUseCabPassCompactScroll(isMobileOrTabletProp?: boolean): boolean {
  if (isMobileOrTabletProp) return true;
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) return false;
    if (window.matchMedia("(max-width: 1024px)").matches) return true;
    if (window.matchMedia("(pointer: coarse)").matches) return true;
    if (window.matchMedia("(hover: none)").matches) return true;
  } catch {
    /* ignore */
  }
  return false;
}

/** Nearest overflow-y scroll parents (including .rv-main-content and document). */
function cabPassScrollParents(from: HTMLElement): HTMLElement[] {
  const found: HTMLElement[] = [];
  const seen = new Set<HTMLElement>();
  let node: HTMLElement | null = from;
  while (node) {
    const sty = window.getComputedStyle(node);
    const oy = sty.overflowY;
    const canY = oy === "auto" || oy === "scroll" || oy === "overlay";
    if (canY && node.scrollHeight > node.clientHeight + 1 && !seen.has(node)) {
      found.push(node);
      seen.add(node);
    }
    node = node.parentElement;
  }
  const main = employeeProfileScrollRoot(from);
  if (main && !seen.has(main)) found.unshift(main);
  const doc = document.scrollingElement as HTMLElement | null;
  if (doc && !seen.has(doc)) found.push(doc);
  return found;
}

/** Sticky app chrome (header + “Viewing as…”) height for scroll offsets. */
function cabPassStickyChromeOffset(): number {
  if (typeof document === "undefined") return 160;
  const header = document.querySelector<HTMLElement>(".rv-main-header");
  const bottom = header?.getBoundingClientRect().bottom ?? 0;
  // Header + Viewing-as banner commonly sit ~120–180px on iPhone.
  return Math.max(Math.ceil(bottom) + 16, 160);
}

/**
 * iOS-reliable scroll: move the main overflow parent by the delta needed to place
 * `el` just below sticky chrome (same approach as login MFA).
 */
function scrollEmployeeProfileToElement(
  el: HTMLElement,
  opts?: { offset?: number; behavior?: ScrollBehavior }
) {
  const offset = opts?.offset ?? cabPassStickyChromeOffset();
  const behavior = opts?.behavior ?? "smooth";
  const applyDelta = () => {
    const rect = el.getBoundingClientRect();
    const delta = rect.top - offset;
    if (Math.abs(delta) < 1) return;
    const parents = cabPassScrollParents(el);
    if (parents.length === 0) {
      window.scrollBy(0, delta);
      return;
    }
    // Prefer the main profile scroller; fall back through parents.
    const main = employeeProfileScrollRoot(el);
    const primary = main && parents.includes(main) ? main : parents[0]!;
    if (behavior === "auto") {
      primary.scrollTop = Math.max(0, primary.scrollTop + delta);
      primary.scrollLeft = 0;
    } else {
      primary.scrollTo({
        top: Math.max(0, primary.scrollTop + delta),
        left: 0,
        behavior: "auto", // smooth fights sticky retries on iOS
      });
      primary.scrollLeft = 0;
    }
    // Second pass after layout — iOS often needs a remeasure.
    const rect2 = el.getBoundingClientRect();
    const delta2 = rect2.top - offset;
    if (Math.abs(delta2) > 2) {
      primary.scrollTop = Math.max(0, primary.scrollTop + delta2);
    }
    // If still wrong, nudge window / document scroller too (iOS Safari quirk).
    const rect3 = el.getBoundingClientRect();
    const delta3 = rect3.top - offset;
    if (Math.abs(delta3) > 4) {
      window.scrollBy(0, delta3);
      const doc = document.scrollingElement;
      if (doc) doc.scrollTop = Math.max(0, doc.scrollTop + delta3);
    }
  };
  applyDelta();
  document.documentElement.scrollLeft = 0;
  document.body.scrollLeft = 0;
}

/** Focus a field and keep it near the top of the profile viewport (not the page bottom). */
function focusCabPassFieldNearTop(
  el: HTMLElement | null | undefined,
  opts?: { mobile?: boolean; offset?: number }
) {
  if (!el) return;
  const mobile = Boolean(opts?.mobile);
  const offset = opts?.offset ?? (mobile ? 100 : 110);
  el.focus({ preventScroll: true });
  scrollEmployeeProfileToElement(el, {
    offset,
    behavior: mobile ? "auto" : "smooth",
  });
}

/**
 * Focus a field and keep re-asserting scroll for a short window so browser autofill /
 * keyboard / parent re-renders cannot yank the user to the cab-pass (licence) preview.
 */
function focusCabPassFieldMobileSticky(
  el: HTMLElement | null | undefined,
  opts?: { offset?: number; holdMs?: number; focus?: boolean }
): () => void {
  if (!el) return () => {};
  const offset = opts?.offset ?? cabPassStickyChromeOffset();
  const holdMs = opts?.holdMs ?? 2500;
  const doFocus = opts?.focus !== false;
  let stopped = false;

  const apply = () => {
    if (stopped) return;
    if (doFocus) {
      try {
        el.focus({ preventScroll: true });
      } catch {
        /* ignore */
      }
    }
    scrollEmployeeProfileToElement(el, { offset, behavior: "auto" });
  };

  apply();
  const retryMs = [0, 16, 50, 100, 180, 300, 450, 700, 1000, 1400, 2000, 2400, 3000];
  const timers = retryMs.filter((ms) => ms <= holdMs).map((ms) => window.setTimeout(apply, ms));

  const onScroll = () => {
    if (stopped) return;
    const rect = el.getBoundingClientRect();
    // If the target drifted well away from the top band, snap it back.
    if (rect.top < offset - 80 || rect.top > offset + 140) {
      apply();
    }
  };
  const mains = cabPassScrollParents(el);
  mains.forEach((m) => m.addEventListener("scroll", onScroll, { passive: true }));

  const onVv = () => {
    if (stopped) return;
    apply();
  };
  window.visualViewport?.addEventListener("resize", onVv);
  window.visualViewport?.addEventListener("scroll", onVv);

  const stopTimer = window.setTimeout(() => {
    stopped = true;
    mains.forEach((m) => m.removeEventListener("scroll", onScroll));
    window.visualViewport?.removeEventListener("resize", onVv);
    window.visualViewport?.removeEventListener("scroll", onVv);
  }, holdMs);

  return () => {
    stopped = true;
    timers.forEach((t) => window.clearTimeout(t));
    window.clearTimeout(stopTimer);
    mains.forEach((m) => m.removeEventListener("scroll", onScroll));
    window.visualViewport?.removeEventListener("resize", onVv);
    window.visualViewport?.removeEventListener("scroll", onVv);
  };
}

/**
 * Autofill must never jump the form away from the field being edited.
 * On focus we scroll the field above the keyboard (ensureCabPassFieldVisibleAboveKeyboard);
 * we do not freeze scroll for the whole focus session.
 */

/** Ordered form fields after colour is chosen — advance one step at a time via Enter only. */
const CAB_PASS_MOBILE_FIELD_ORDER = [
  "valid-from",
  "valid-to",
  "issued-by-name",
  "issuer-department",
  "validity-scope",
  "endorsements",
  "preview",
] as const;

type CabPassMobileField = (typeof CAB_PASS_MOBILE_FIELD_ORDER)[number];

function cabPassMobileFieldSelector(passId: string, field: CabPassMobileField): string {
  return `[data-cab-pass-mobile-field="${passId}:${field}"]`;
}

function findCabPassMobileField(passId: string, field: CabPassMobileField): HTMLElement | null {
  return document.querySelector<HTMLElement>(cabPassMobileFieldSelector(passId, field));
}

/** Advance exactly one field — Issued by → Issuer department, …, Endorsements → preview. */
function focusNextCabPassMobileField(
  passId: string,
  current: CabPassMobileField,
  opts?: { holdMs?: number }
): () => void {
  const idx = CAB_PASS_MOBILE_FIELD_ORDER.indexOf(current);
  if (idx < 0 || idx >= CAB_PASS_MOBILE_FIELD_ORDER.length - 1) return () => {};
  const next = CAB_PASS_MOBILE_FIELD_ORDER[idx + 1];
  const el = findCabPassMobileField(passId, next);
  if (!el) return () => {};
  // Preview is scroll-only (not a focusable input) and only reached from endorsements Enter.
  if (next === "preview") {
    return focusCabPassFieldMobileSticky(el, {
      offset: cabPassStickyChromeOffset(),
      holdMs: opts?.holdMs ?? 1200,
      focus: false,
    });
  }
  return focusCabPassFieldMobileSticky(el, {
    offset: cabPassStickyChromeOffset(),
    holdMs: opts?.holdMs ?? 2500,
    focus: true,
  });
}

/** After colour Continue — land on Valid from and keep it pinned (no date focus on iOS). */
function focusCabPassValidFromField(passId: string): () => void {
  const offset = cabPassStickyChromeOffset();
  const anchor =
    document.querySelector<HTMLElement>(`[data-cab-pass-valid-from-anchor="${passId}"]`) ||
    findCabPassMobileField(passId, "valid-from") ||
    document.querySelector<HTMLElement>(`[data-cab-pass-first-field="${passId}"]`);
  if (!anchor) {
    const card = document.querySelector<HTMLElement>(`[data-cab-pass-card="${passId}"]`);
    if (card) {
      return focusCabPassFieldMobileSticky(card, { offset, holdMs: 3200, focus: false });
    }
    return () => {};
  }
  // Do not focus type=date — iOS opens the picker and cancels our scroll.
  try {
    anchor.scrollIntoView({ block: "start", inline: "nearest", behavior: "auto" });
  } catch {
    /* ignore */
  }
  scrollEmployeeProfileToElement(anchor, { offset, behavior: "auto" });
  return focusCabPassFieldMobileSticky(anchor, { offset, holdMs: 3600, focus: false });
}

function captureEmployeeProfileScroll(from?: HTMLElement | null): { main: HTMLElement | null; top: number; left: number } {
  const main = employeeProfileScrollRoot(from);
  if (main) return { main, top: main.scrollTop, left: main.scrollLeft };
  return {
    main: null,
    top: typeof window !== "undefined" ? window.scrollY : 0,
    left: typeof window !== "undefined" ? window.scrollX : 0,
  };
}

function restoreEmployeeProfileScroll(saved: { main: HTMLElement | null; top: number; left: number }) {
  if (saved.main) {
    saved.main.scrollTop = saved.top;
    // Never restore horizontal — signature/preview remounts must not drift left/right.
    saved.main.scrollLeft = 0;
  } else if (typeof window !== "undefined") {
    window.scrollTo(0, saved.top);
  }
  resetEmployeeProfileHorizontalScroll();
}

function resetEmployeeProfileHorizontalScroll() {
  document.querySelectorAll<HTMLElement>(".rv-main-content").forEach((el) => {
    el.scrollLeft = 0;
  });
  document.querySelectorAll<HTMLElement>("[data-cab-passes-tab], [data-cab-pass-card]").forEach((el) => {
    el.scrollLeft = 0;
  });
  document.documentElement.scrollLeft = 0;
  document.body.scrollLeft = 0;
  if (typeof window !== "undefined" && window.scrollX !== 0) {
    window.scrollTo(0, window.scrollY);
  }
  // iOS pans visualViewport.offsetLeft when focusing inputs — that is the grey-bar "container drift".
  const vv = typeof window !== "undefined" ? window.visualViewport : null;
  if (vv && Math.abs(vv.offsetLeft) > 0.5 && typeof vv.scrollTo === "function") {
    try {
      vv.scrollTo(0, vv.offsetTop);
    } catch {
      /* ignore */
    }
  }
  const leftover = window.visualViewport?.offsetLeft ?? 0;
  document.documentElement.style.setProperty("--rv-cab-pass-vv-left", `${leftover}px`);
}

/**
 * Keep horizontal scroll pinned hard (signature apply / preview paint).
 * Also clamps nested overflow parents that iOS may scroll instead of .rv-main-content.
 */
function pinCabPassHorizontalScroll(holdMs = 2000): () => void {
  let stopped = false;
  const apply = () => {
    if (stopped) return;
    resetEmployeeProfileHorizontalScroll();
    // Walk overflow-x parents under the cab passes tab and zero them.
    document.querySelectorAll<HTMLElement>("[data-cab-passes-tab] *").forEach((el) => {
      if (el.scrollLeft) el.scrollLeft = 0;
    });
  };
  apply();
  const timers = [0, 16, 32, 50, 80, 120, 180, 250, 350, 500, 700, 1000, 1400, 1800]
    .filter((ms) => ms <= holdMs)
    .map((ms) => window.setTimeout(apply, ms));
  const onScroll = () => apply();
  const mains = Array.from(document.querySelectorAll<HTMLElement>(".rv-main-content"));
  mains.forEach((el) => el.addEventListener("scroll", onScroll, { passive: true }));
  window.addEventListener("scroll", onScroll, { passive: true });
  window.visualViewport?.addEventListener("resize", onScroll);
  window.visualViewport?.addEventListener("scroll", onScroll);
  const stopTimer = window.setTimeout(() => {
    stopped = true;
    mains.forEach((el) => el.removeEventListener("scroll", onScroll));
    window.removeEventListener("scroll", onScroll);
    window.visualViewport?.removeEventListener("resize", onScroll);
    window.visualViewport?.removeEventListener("scroll", onScroll);
  }, holdMs);
  return () => {
    stopped = true;
    timers.forEach((t) => window.clearTimeout(t));
    window.clearTimeout(stopTimer);
    mains.forEach((el) => el.removeEventListener("scroll", onScroll));
    window.removeEventListener("scroll", onScroll);
    window.visualViewport?.removeEventListener("resize", onScroll);
    window.visualViewport?.removeEventListener("scroll", onScroll);
  };
}

/** iOS keyboard height (layout viewport minus visual viewport). */
function cabPassKeyboardInsetPx(): number {
  const vv = window.visualViewport;
  if (!vv) return 0;
  return Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
}

/**
 * On focus: add keyboard pad + scroll the field into the visible band once.
 * While typing: do NOT keep re-centering (that yanked Issuer department off-screen
 * on every keystroke). Only re-lift if the field is actually covered by the keyboard.
 */
function ensureCabPassFieldVisibleAboveKeyboard(el: HTMLElement, holdMs = 1600): () => void {
  let stopped = false;
  const tab = el.closest<HTMLElement>("[data-cab-passes-tab]");
  const main = employeeProfileScrollRoot(el);
  // Pad only the scroll root — padding both main + tab double-expands layout and looks like drift.
  const padTargets = [main ?? tab].filter((n, i, arr): n is HTMLElement => !!n && arr.indexOf(n) === i);
  const prevPads = padTargets.map((node) => ({
    node,
    paddingBottom: node.style.paddingBottom,
  }));

  const fieldBlock =
    (el.closest("[data-cab-pass-field-block]") as HTMLElement | null) ||
    (el.parentElement as HTMLElement | null) ||
    el;

  const syncKeyboardPad = () => {
    const inset = cabPassKeyboardInsetPx();
    const accessory = inset > 40 ? 110 : 0;
    const pad = Math.max(inset > 40 ? inset + accessory : 220, 280);
    for (const node of padTargets) {
      node.style.paddingBottom = `${pad}px`;
      node.classList.add("rv-cab-pass-keyboard-open");
    }
    document.documentElement.style.setProperty("--rv-cab-pass-keyboard-pad", `${pad}px`);
  };

  const clearKeyboardPad = () => {
    for (const { node, paddingBottom } of prevPads) {
      node.style.paddingBottom = paddingBottom;
      node.classList.remove("rv-cab-pass-keyboard-open");
    }
    document.documentElement.style.removeProperty("--rv-cab-pass-keyboard-pad");
    document.documentElement.style.setProperty("--rv-cab-pass-vv-left", "0px");
  };

  /** Only scroll when the field is covered / clipped — never snap to a fixed top on every tick. */
  const apply = (force = false) => {
    if (stopped || !el.isConnected) return;
    syncKeyboardPad();
    const vv = window.visualViewport;
    const vvTop = vv?.offsetTop ?? 0;
    const vvHeight = vv?.height ?? window.innerHeight;
    const chrome = Math.min(cabPassStickyChromeOffset(), Math.floor(vvHeight * 0.36));
    const accessoryInsideVv = cabPassKeyboardInsetPx() > 40 ? 100 : 28;
    const visibleTop = vvTop + Math.max(12, chrome * 0.55);
    const visibleBottom = vvTop + vvHeight - accessoryInsideVv;
    const rect = fieldBlock.getBoundingClientRect();

    const coveredByKeyboard = rect.bottom > visibleBottom + 4;
    const clippedByChrome = rect.top < visibleTop - 4;
    const fullyGone = rect.bottom < vvTop + 8 || rect.top > vvTop + vvHeight - 8;

    if (!force && !coveredByKeyboard && !clippedByChrome && !fullyGone) {
      resetEmployeeProfileHorizontalScroll();
      return;
    }

    // Place the field in the upper-middle of the visible keyboard viewport (stable while typing).
    const desiredTop = vvTop + Math.min(chrome, Math.floor(vvHeight * 0.28));
    let delta = rect.top - desiredTop;
    // Prefer clearing the keyboard if both constraints conflict.
    if (rect.bottom - delta > visibleBottom) {
      delta = rect.bottom - visibleBottom;
    }
    if (Math.abs(delta) < 4) {
      resetEmployeeProfileHorizontalScroll();
      return;
    }

    const parents = cabPassScrollParents(el);
    const primary = main && parents.includes(main) ? main : parents[0];
    if (primary) {
      primary.scrollTop = Math.max(0, primary.scrollTop + delta);
      primary.scrollLeft = 0;
    } else {
      window.scrollBy(0, delta);
    }

    const rect2 = fieldBlock.getBoundingClientRect();
    if (rect2.bottom > visibleBottom + 4) {
      const lift = rect2.bottom - visibleBottom;
      if (primary) primary.scrollTop = Math.max(0, primary.scrollTop + lift);
      else window.scrollBy(0, lift);
    }
    resetEmployeeProfileHorizontalScroll();
  };

  syncKeyboardPad();
  // Initial bring-into-view (keyboard often still animating).
  apply(true);
  const timers = [50, 120, 280, 500, 900]
    .filter((ms) => ms <= holdMs)
    .map((ms) => window.setTimeout(() => apply(false), ms));

  // Only re-check when the keyboard size changes — not on every visualViewport scroll
  // (iOS fires those constantly while typing and was yanking the field away).
  const onVvResize = () => {
    if (stopped) return;
    apply(false);
  };
  window.visualViewport?.addEventListener("resize", onVvResize);
  const stopTimer = window.setTimeout(() => {
    window.visualViewport?.removeEventListener("resize", onVvResize);
  }, holdMs);

  return () => {
    stopped = true;
    timers.forEach((t) => window.clearTimeout(t));
    window.clearTimeout(stopTimer);
    window.visualViewport?.removeEventListener("resize", onVvResize);
    clearKeyboardPad();
    resetEmployeeProfileHorizontalScroll();
  };
}

function normCabPassPersonName(raw?: string): string {
  return String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

const RENEW_FORBIDDEN_MSG =
  "You need the Cab passes Renew or Edit role permission, to be the employee's assigned assessing manager (with Cab passes view), or be a company administrator. Change the assessing manager under Main profile or ask your company administrator to update Role permissions.";

/** Opaque white backing so strokes stay visible in dark UI and exports aren’t transparent-on-dark. */
function fillCanvasWhite(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
}

function SignaturePad({
  value,
  canEdit,
  isDarkMode,
  onChange,
  onApplied,
}: {
  value?: string;
  canEdit: boolean;
  isDarkMode: boolean;
  onChange: (next?: string) => void;
  /** After apply — bump preview paint on mobile WebKit. */
  onApplied?: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const [dirty, setDirty] = useState(false);
  /** Show drawing pad only when there is no saved image or user chose Replace. */
  const [padOpen, setPadOpen] = useState(!value);

  useEffect(() => {
    if (!value) setPadOpen(true);
    else setPadOpen(false);
  }, [value]);

  useEffect(() => {
    if (!canEdit || !padOpen) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    fillCanvasWhite(canvas);
  }, [canEdit, padOpen, value]);

  const getPoint = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    return { x: (e.clientX - rect.left) * sx, y: (e.clientY - rect.top) * sy };
  };

  const startDraw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!canEdit) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    const p = getPoint(e);
    if (!ctx || !p) return;
    drawingRef.current = true;
    canvas.setPointerCapture(e.pointerId);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 2.2;
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };

  const moveDraw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!canEdit || !drawingRef.current) return;
    const ctx = canvasRef.current?.getContext("2d");
    const p = getPoint(e);
    if (!ctx || !p) return;
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    setDirty(true);
  };

  const endDraw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!canEdit) return;
    if (drawingRef.current) {
      drawingRef.current = false;
      canvasRef.current?.releasePointerCapture(e.pointerId);
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    fillCanvasWhite(canvas);
    setDirty(false);
  };

  const applySignature = () => {
    const canvas = canvasRef.current;
    if (!canvas || !dirty) return;
    // Applying closes the pad and remounts the preview — keep vertical position; pin X.
    const savedScroll = captureEmployeeProfileScroll(canvas);
    const dataUrl = canvas.toDataURL("image/png");
    flushSync(() => {
      onChange(dataUrl);
    });
    onApplied?.();
    setDirty(false);
    setPadOpen(false);
    const keepScroll = () => {
      restoreEmployeeProfileScroll(savedScroll);
      resetEmployeeProfileHorizontalScroll();
    };
    keepScroll();
    requestAnimationFrame(() => {
      keepScroll();
      requestAnimationFrame(keepScroll);
    });
    // Hold horizontal lock while preview paints (avoids left-right drift).
    pinCabPassHorizontalScroll(2400);
  };

  return (
    <div className="space-y-2">
      {value ? (
        <div
          className={cn(
            "inline-block max-w-full rounded-lg border p-2",
            isDarkMode ? "border-slate-600 bg-white" : "border-slate-200 bg-white"
          )}
        >
          <img src={value} alt={translateUi("Issuer signature")} className="max-h-16 w-auto max-w-full object-contain" />
        </div>
      ) : (
        <p className={cn("text-sm", isDarkMode ? "text-slate-500" : "text-slate-500")}>{translateUi("No signature yet.")}</p>
      )}
      {canEdit ? (
        <>
          {padOpen ? (
            <>
              <canvas
                ref={canvasRef}
                width={420}
                height={120}
                onPointerDown={startDraw}
                onPointerMove={moveDraw}
                onPointerUp={endDraw}
                onPointerCancel={endDraw}
                className={cn(
                  "block h-[90px] w-full max-w-full touch-none rounded-lg border bg-white",
                  isDarkMode ? "border-slate-600" : "border-slate-300"
                )}
              />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={clearCanvas}
                  className={cn(
                    "rounded-lg border px-3 py-1.5 text-xs font-semibold",
                    isDarkMode ? "border-slate-500 text-slate-200 hover:bg-slate-700" : "border-slate-300 text-slate-700 hover:bg-slate-50"
                  )}
                >
                  {translateUi("Clear signature")}
                </button>
                <button
                  type="button"
                  onClick={applySignature}
                  disabled={!dirty}
                  className={cn(
                    "rounded-lg border px-3 py-1.5 text-xs font-semibold disabled:opacity-50",
                    isDarkMode
                      ? "border-emerald-500/50 text-emerald-200 hover:bg-emerald-500/10"
                      : "border-emerald-200 text-emerald-800 hover:bg-emerald-50"
                  )}
                >
                  {translateUi("Apply signature")}
                </button>
                {value ? (
                  <button
                    type="button"
                    onClick={() => onChange(undefined)}
                    className={cn(
                      "rounded-lg border px-3 py-1.5 text-xs font-semibold",
                      isDarkMode
                        ? "border-rose-500/50 text-rose-300 hover:bg-rose-500/10"
                        : "border-rose-200 text-rose-700 hover:bg-rose-50"
                    )}
                  >
                    {translateUi("Remove saved signature")}
                  </button>
                ) : null}
              </div>
              <p className={cn("text-xs", isDarkMode ? "text-slate-500" : "text-slate-500")}>{translateUi("Sign using mouse, trackpad, or touch.")}</p>
            </>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setPadOpen(true)}
                className={cn(
                  "rounded-lg border px-3 py-1.5 text-xs font-semibold",
                  isDarkMode ? "border-slate-500 text-slate-200 hover:bg-slate-700" : "border-slate-300 text-slate-700 hover:bg-slate-50"
                )}
              >
                {translateUi("Replace signature")}
              </button>
              {value ? (
                <button
                  type="button"
                  onClick={() => onChange(undefined)}
                  className={cn(
                    "rounded-lg border px-3 py-1.5 text-xs font-semibold",
                    isDarkMode
                      ? "border-rose-500/50 text-rose-300 hover:bg-rose-500/10"
                      : "border-rose-200 text-rose-700 hover:bg-rose-50"
                  )}
                >
                  {translateUi("Remove saved signature")}
                </button>
              ) : null}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

export type CabPassColor = "green" | "yellow" | "blue" | "red" | "black";

export type CabPassRecord = {
  id: string;
  pass_color: CabPassColor;
  pass_number: string;
  valid_from: string;
  valid_to: string;
  issued_by_name: string;
  issued_by_department: string;
  /** Legacy holder department; unused in the form — kept so old JSON still parses. */
  holder_department?: string;
  endorsements: string;
  issuer_signature_url?: string;
  image_url?: string;
  /** Routes / area of validity (shown on pass like PDF “VALIDITY” block). */
  validity_scope?: string;
  /** When true, `verification_token` is set and the pass can be checked via the public verify URL. */
  digital?: boolean;
  verification_token?: string;
  /** Short secret for optional `?s=` verify URL (server-assigned). */
  scan_code?: string;
  /** ISO timestamp when the digital pass was first created. */
  digital_created_at?: string;
  /** Email of the user who first created the digital pass. */
  digital_created_by?: string;
  /** Display name of the user who first created the digital pass. */
  digital_created_by_name?: string;
};

const CAB_PASS_SCAN_CODE_RE = /^[a-f0-9]{16}$/i;

const CAB_PASS_COLOR_OPTIONS: { value: CabPassColor; label: string; swatch: string; description: string }[] = [
  { value: "green", label: "Green", swatch: "bg-emerald-500", description: "Authorised to Assess" },
  { value: "yellow", label: "Yellow", swatch: "bg-amber-400", description: "PTS (Personal Track Safety) competent" },
  { value: "blue", label: "Blue", swatch: "bg-sky-600", description: "Operational Rules competent" },
  {
    value: "red",
    label: "Red",
    swatch: "bg-red-600",
    description: "Issued with endorsements; usually accompanied by a green cab pass holder",
  },
  { value: "black", label: "Black", swatch: "bg-slate-900", description: "Typically used for route learning" },
];

function cabPassDeptOnCard(pass: CabPassRecord): string {
  return String(pass.issued_by_department ?? "").trim() || String(pass.holder_department ?? "").trim();
}

function formatDateUK(value: string): string {
  if (!value?.trim()) return "—";
  const datePart = value.split("T")[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    const [y, m, d] = datePart.split("-");
    return `${d}-${m}-${y}`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
}

/** UK date + time for digital-pass audit stamps (ISO or datetime strings). */
function formatDateTimeUK(value: string): string {
  if (!value?.trim()) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    const dateOnly = formatDateUK(value);
    return dateOnly === "—" ? value.trim() : dateOnly;
  }
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
}

/** Prefer a person's display name — never fall back to an email address in the UI. */
function digitalPassCreatorLabel(
  pass: Pick<CabPassRecord, "digital_created_by" | "digital_created_by_name">,
  currentUserEmail?: string,
  currentUserFullName?: string
): string | null {
  const storedName = String(pass.digital_created_by_name ?? "").trim();
  if (storedName && !storedName.includes("@")) return storedName;
  const byEmail = String(pass.digital_created_by ?? "").trim().toLowerCase();
  const me = String(currentUserEmail ?? "").trim().toLowerCase();
  const myName = String(currentUserFullName ?? "").trim();
  if (byEmail && me && byEmail === me && myName && !myName.includes("@")) return myName;
  return null;
}

export function parseCabPassesJsonClient(raw: string | undefined | null): CabPassRecord[] {
  if (!raw || !String(raw).trim()) return [];
  try {
    const p = JSON.parse(String(raw)) as unknown;
    if (!Array.isArray(p)) return [];
    const colors = new Set<string>(["green", "yellow", "blue", "red", "black"]);
    const out: CabPassRecord[] = [];
    for (const row of p) {
      if (!row || typeof row !== "object") continue;
      const o = row as Record<string, unknown>;
      const id = String(o.id ?? "").trim();
      const c = String(o.pass_color ?? "").trim().toLowerCase();
      if (!id || !colors.has(c)) continue;
      const digital =
        o.digital === true || o.digital === 1 || String(o.digital ?? "").toLowerCase() === "true";
      const verification_token = String(o.verification_token ?? "").trim();
      out.push({
        id,
        pass_color: c as CabPassColor,
        pass_number: String(o.pass_number ?? "").trim(),
        valid_from: String(o.valid_from ?? "").trim(),
        valid_to: String(o.valid_to ?? "").trim(),
        // Do not trim free-text fields — trimming strips trailing spaces mid-typing
        // (e.g. "Ops " → "Ops") and makes multi-word names impossible.
        issued_by_name: String(o.issued_by_name ?? ""),
        issued_by_department: String(o.issued_by_department ?? ""),
        // Always keep as string (including empty) so controlled inputs stay stable across saves.
        holder_department: typeof o.holder_department === "string" ? o.holder_department : String(o.holder_department ?? ""),
        endorsements: String(o.endorsements ?? ""),
        // Always keep as string (including empty / trailing spaces) so controlled inputs stay stable.
        validity_scope: typeof o.validity_scope === "string" ? o.validity_scope : String(o.validity_scope ?? ""),
        ...(typeof o.issuer_signature_url === "string" && String(o.issuer_signature_url).length > 0
          ? { issuer_signature_url: String(o.issuer_signature_url) }
          : {}),
        ...(typeof o.image_url === "string" && o.image_url.trim() ? { image_url: String(o.image_url) } : {}),
        ...(digital && verification_token
          ? {
              digital: true,
              verification_token,
              ...(typeof o.scan_code === "string" && CAB_PASS_SCAN_CODE_RE.test(String(o.scan_code).trim())
                ? { scan_code: String(o.scan_code).trim().toLowerCase() }
                : {}),
            }
          : {}),
        ...(typeof o.digital_created_at === "string" && String(o.digital_created_at).trim()
          ? { digital_created_at: String(o.digital_created_at).trim() }
          : {}),
        ...(typeof o.digital_created_by === "string" && String(o.digital_created_by).trim()
          ? { digital_created_by: String(o.digital_created_by).trim() }
          : {}),
        ...(typeof o.digital_created_by_name === "string" && String(o.digital_created_by_name).trim()
          ? { digital_created_by_name: String(o.digital_created_by_name).trim() }
          : {}),
      });
    }
    return out;
  } catch {
    return [];
  }
}

function newCabPassId(): string {
  return `cp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

function randomVerifyToken(): string {
  const a = new Uint8Array(24);
  crypto.getRandomValues(a);
  return Array.from(a, (x) => x.toString(16).padStart(2, "0")).join("");
}

/** 16 hex chars — short public `s` query for mobile-friendly CODE128. */
function randomCabPassScanCode(): string {
  const a = new Uint8Array(8);
  crypto.getRandomValues(a);
  return Array.from(a, (x) => x.toString(16).padStart(2, "0")).join("");
}

/** Random 8-digit cab pass number, unique among the employee's existing passes. */
function generateUniqueCabPassNumber(existing: CabPassRecord[]): string {
  const taken = new Set(
    existing.map((p) => String(p.pass_number ?? "").trim().toLowerCase()).filter(Boolean)
  );
  for (let attempt = 0; attempt < 64; attempt++) {
    const n = new Uint32Array(1);
    crypto.getRandomValues(n);
    const candidate = String(10_000_000 + (n[0]! % 90_000_000));
    if (!taken.has(candidate)) return candidate;
  }
  return `CP-${Date.now().toString(36).toUpperCase()}`;
}

/** Base64url payload for `?d=` verify links (compact for barcodes). */
export function encodeCabPassVerifyPayload(p: {
  companyCode: string;
  employeeId: string;
  passId: string;
  token: string;
}): string {
  const json = JSON.stringify({
    c: p.companyCode,
    e: p.employeeId,
    p: p.passId,
    t: p.token,
  });
  const b64 = btoa(unescape(encodeURIComponent(json)));
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/u, "");
}

/**
 * Public cab pass verify link. Prefer 16-char `?s=` when `scanCode` is set — much
 * shorter than `?d=` (dense QR, hard to scan on printed cards / phones).
 */
export function buildCabPassVerifyUrl(
  origin: string,
  p: {
    companyCode: string;
    employeeId: string;
    passId: string;
    token: string;
    scanCode?: string;
  }
): string {
  const base = (origin || "").replace(/\/+$/, "");
  const sc = String(p.scanCode ?? "").trim().toLowerCase();
  if (CAB_PASS_SCAN_CODE_RE.test(sc)) {
    return `${base}/api/public/cab-pass-verify?s=${encodeURIComponent(sc)}`;
  }
  const d = encodeCabPassVerifyPayload(p);
  return `${base}/api/public/cab-pass-verify?d=${encodeURIComponent(d)}`;
}

function isPassDateRangeOk(validFrom: string, validTo: string): boolean {
  const today = cabPassCalendarToday();
  const vf = String(validFrom ?? "").trim().split("T")[0];
  const vt = String(validTo ?? "").trim().split("T")[0];
  if (vf && /^\d{4}-\d{2}-\d{2}$/.test(vf) && today < vf) return false;
  if (vt && /^\d{4}-\d{2}-\d{2}$/.test(vt) && today > vt) return false;
  return true;
}

export function CabPassesTab({
  employee,
  companyCode,
  cabPassesJson,
  canInteractWithCabPassTab,
  cabPassesManagePermission = false,
  cabPassPermissionEdit = false,
  cabPassPermissionDelete = false,
  cabPassPermissionRevoke = false,
  cabPassPermissionRenew = false,
  cabPassCompanyAdminActions = false,
  currentUserFullName,
  currentUserEmail,
  isDarkMode,
  isMobileOrTablet = false,
  onCabPassesJsonChange,
  onCommitCabPassesNow,
}: {
  employee: CabPassesTabEmployee;
  companyCode: string | undefined;
  cabPassesJson: string | undefined;
  /** User may open this tab (baseline view and/or granular cab-pass roles). */
  canInteractWithCabPassTab: boolean;
  /** cab-passes.manage — baseline visibility / legacy delegated renew with assessing manager match. */
  cabPassesManagePermission?: boolean;
  /** Role permissions assigned under Company administration → Role permissions. */
  cabPassPermissionEdit?: boolean;
  cabPassPermissionDelete?: boolean;
  cabPassPermissionRevoke?: boolean;
  cabPassPermissionRenew?: boolean;
  /** Company administrator or system administrator — unrestricted cab-pass UI (server still enforces). */
  cabPassCompanyAdminActions?: boolean;
  /** Signed-in user display name — compared to employee.manager for renew eligibility. */
  currentUserFullName?: string;
  /** Signed-in user email — required for issue-digital API auth. */
  currentUserEmail?: string;
  isDarkMode: boolean;
  isMobileOrTablet?: boolean;
  onCabPassesJsonChange: (json: string) => void;
  /** Persist cab_passes_json immediately (e.g. after creating a digital pass so verification works before profile Save). */
  onCommitCabPassesNow?: (json: string) => Promise<boolean>;
}) {
  const parsedFromProp = useMemo(() => parseCabPassesJsonClient(cabPassesJson), [cabPassesJson]);
  const [passes, setLocalPasses] = useState(parsedFromProp);
  const assessingManagerRenewMatch = useMemo(() => {
    const m = normCabPassPersonName(employee.manager);
    const u = normCabPassPersonName(currentUserFullName);
    return m.length > 0 && u.length > 0 && m === u;
  }, [employee.manager, currentUserFullName]);

  const mayEditCabPasses = Boolean(cabPassCompanyAdminActions || cabPassPermissionEdit);
  const mayDeleteCabPasses = Boolean(cabPassCompanyAdminActions || cabPassPermissionDelete);
  const mayRevokeCabPasses = Boolean(cabPassCompanyAdminActions || cabPassPermissionRevoke);

  /** Edit includes date/token changes server-side; renew permission is narrower (renew-only). */
  const cabPassRenewAllowed = Boolean(
    cabPassCompanyAdminActions ||
      assessingManagerRenewMatch ||
      cabPassPermissionRenew ||
      cabPassPermissionEdit
  );
  const commitsCabPassesToServer = typeof onCommitCabPassesNow === "function";
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const cc = String(companyCode || employee.company_code || "").trim();
  const [expandedPasses, setExpandedPasses] = useState<Record<string, boolean>>({});
  const [readonlyPasses, setReadonlyPasses] = useState<Record<string, boolean>>({});
  const [renewingPassId, setRenewingPassId] = useState<string | null>(null);
  const [renewFrom, setRenewFrom] = useState("");
  const [renewTo, setRenewTo] = useState("");
  /** Colour picker before creating a new pass — never assume green. */
  const [addPassColorOpen, setAddPassColorOpen] = useState(false);
  const [pendingPassColor, setPendingPassColor] = useState<CabPassColor | null>(null);
  /** After adding a pass, scroll this card (expanded details) into view. */
  const [scrollToPassId, setScrollToPassId] = useState<string | null>(null);
  const passCardElsRef = useRef<Record<string, HTMLDivElement | null>>({});
  /** Cancels in-flight mobile sticky focus (colour Continue / Enter field advance). */
  const mobileFieldFocusCleanupRef = useRef<(() => void) | null>(null);
  /** Cancels focus-scoped scroll lock (Issued by / department fields). */
  const fieldScrollLockCleanupRef = useRef<(() => void) | null>(null);
  /**
   * On compact/iOS: keep the live licence/card preview unmounted while filling early
   * fields so Safari cannot scroll-anchor down to it.
   */
  const [mobilePreviewUnlocked, setMobilePreviewUnlocked] = useState<Record<string, boolean>>({});
  const horizontalPinCleanupRef = useRef<(() => void) | null>(null);
  const [deleteConfirmPassId, setDeleteConfirmPassId] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  /** Confirm issue digital + email to employee. */
  const [digitalIssuePassId, setDigitalIssuePassId] = useState<string | null>(null);
  const [digitalIssueBusy, setDigitalIssueBusy] = useState(false);
  const [digitalIssueResult, setDigitalIssueResult] = useState<null | { ok: boolean; message: string }>(null);
  /** Bumps cab pass preview remount on mobile after signature apply. */
  const [cabPassPreviewGen, setCabPassPreviewGen] = useState(0);
  /** Latest passes for rapid keystrokes (avoids stale closure wiping characters). */
  const passesRef = useRef(passes);
  passesRef.current = passes;
  const cabPassCommitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const parentSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingCabPassCommitJsonRef = useRef<string | null>(null);
  /** JSON we last pushed via onCabPassesJsonChange — ignore prop echo so typing is not re-parsed. */
  const lastEmittedCabPassesJsonRef = useRef<string | null>(
    cabPassesJson != null ? String(cabPassesJson) : null
  );
  const onCabPassesJsonChangeRef = useRef(onCabPassesJsonChange);
  onCabPassesJsonChangeRef.current = onCabPassesJsonChange;
  const onCommitCabPassesNowRef = useRef(onCommitCabPassesNow);
  onCommitCabPassesNowRef.current = onCommitCabPassesNow;

  // Sync from parent only when the change is external (not our own emit).
  useEffect(() => {
    const incoming = cabPassesJson == null ? "" : String(cabPassesJson);
    if (incoming === (lastEmittedCabPassesJsonRef.current ?? "")) return;
    lastEmittedCabPassesJsonRef.current = incoming;
    setLocalPasses(parsedFromProp);
    passesRef.current = parsedFromProp;
  }, [cabPassesJson, parsedFromProp]);

  // Backfill display name on passes that only stored the creator email.
  useEffect(() => {
    const me = String(currentUserEmail ?? "").trim().toLowerCase();
    const myName = String(currentUserFullName ?? "").trim();
    if (!me || !myName || myName.includes("@")) return;
    const current = passesRef.current;
    let changed = false;
    const next = current.map((p) => {
      const by = String(p.digital_created_by ?? "").trim().toLowerCase();
      const name = String(p.digital_created_by_name ?? "").trim();
      if (!p.digital || by !== me) return p;
      if (name && !name.includes("@")) return p;
      changed = true;
      return { ...p, digital_created_by_name: myName };
    });
    if (!changed) return;
    passesRef.current = next;
    setLocalPasses(next);
    const json = JSON.stringify(next);
    lastEmittedCabPassesJsonRef.current = json;
    onCabPassesJsonChangeRef.current(json);
    if (onCommitCabPassesNowRef.current) {
      void onCommitCabPassesNowRef.current(json);
    }
  }, [currentUserEmail, currentUserFullName, cabPassesJson]);

  const fileSafeIssuedDate = useCallback((raw?: string) => {
    const v = String(raw ?? "").trim().split("T")[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    return new Date().toISOString().slice(0, 10);
  }, []);

  useEffect(() => {
    setExpandedPasses((prev) => {
      const next: Record<string, boolean> = {};
      for (const p of passes) next[p.id] = prev[p.id] ?? false;
      return next;
    });
  }, [passes]);

  const pushParentJson = useCallback((json: string) => {
    lastEmittedCabPassesJsonRef.current = json;
    pendingCabPassCommitJsonRef.current = json;
    onCabPassesJsonChangeRef.current(json);
  }, []);

  const flushCabPassCommit = useCallback(() => {
    if (cabPassCommitTimerRef.current) {
      clearTimeout(cabPassCommitTimerRef.current);
      cabPassCommitTimerRef.current = null;
    }
    if (parentSyncTimerRef.current) {
      clearTimeout(parentSyncTimerRef.current);
      parentSyncTimerRef.current = null;
    }
    const json = JSON.stringify(passesRef.current);
    pushParentJson(json);
    if (commitsCabPassesToServer && onCommitCabPassesNowRef.current) {
      pendingCabPassCommitJsonRef.current = null;
      void onCommitCabPassesNowRef.current(json);
    }
  }, [commitsCabPassesToServer, pushParentJson]);

  // Flush pending text edits only on true unmount (refs avoid effect churn when parent re-creates callbacks).
  useEffect(() => {
    return () => {
      const hadParentSync = parentSyncTimerRef.current != null;
      const hadCommitTimer = cabPassCommitTimerRef.current != null;
      const staged = pendingCabPassCommitJsonRef.current;
      if (cabPassCommitTimerRef.current) {
        clearTimeout(cabPassCommitTimerRef.current);
        cabPassCommitTimerRef.current = null;
      }
      if (parentSyncTimerRef.current) {
        clearTimeout(parentSyncTimerRef.current);
        parentSyncTimerRef.current = null;
      }
      if (!hadParentSync && !hadCommitTimer && staged == null) return;
      const json = JSON.stringify(passesRef.current);
      onCabPassesJsonChangeRef.current(json);
      if (onCommitCabPassesNowRef.current) {
        void onCommitCabPassesNowRef.current(json);
      }
    };
  }, []);

  const setPasses = useCallback(
    (next: CabPassRecord[], opts?: { persist?: "immediate" | "debounced" | "none" }) => {
      passesRef.current = next;
      setLocalPasses(next);
      const persist = opts?.persist ?? "debounced";

      // Typing path: update local preview state only. Do not stringify / push into App on every
      // keystroke (that re-renders the whole employee profile and recreates commit callbacks).
      if (persist === "none") {
        if (parentSyncTimerRef.current) clearTimeout(parentSyncTimerRef.current);
        parentSyncTimerRef.current = setTimeout(() => {
          parentSyncTimerRef.current = null;
          pushParentJson(JSON.stringify(passesRef.current));
        }, 400);
        return;
      }

      const json = JSON.stringify(next);
      pushParentJson(json);
      if (!commitsCabPassesToServer || !onCommitCabPassesNowRef.current) return;
      if (persist === "immediate") {
        if (cabPassCommitTimerRef.current) {
          clearTimeout(cabPassCommitTimerRef.current);
          cabPassCommitTimerRef.current = null;
        }
        pendingCabPassCommitJsonRef.current = null;
        void onCommitCabPassesNowRef.current(json);
        return;
      }
      if (cabPassCommitTimerRef.current) clearTimeout(cabPassCommitTimerRef.current);
      cabPassCommitTimerRef.current = setTimeout(() => {
        flushCabPassCommit();
      }, 500);
    },
    [commitsCabPassesToServer, flushCabPassCommit, pushParentJson]
  );

  const openRenewModal = useCallback(
    (passId: string, passRow: CabPassRecord) => {
      if (!cabPassRenewAllowed) {
        alert(translateUi(RENEW_FORBIDDEN_MSG));
        return;
      }
      setRenewingPassId(passId);
      setRenewFrom(passRow.valid_from || "");
      setRenewTo(passRow.valid_to || "");
    },
    [cabPassRenewAllowed]
  );

  const updatePass = useCallback(
    (id: string, patch: Partial<CabPassRecord>, opts?: { persist?: "immediate" | "debounced" | "none" }) => {
      // Pass numbers are system-assigned and not editable after create.
      const { pass_number: _passNumber, ...safePatch } = patch;
      setPasses(
        passesRef.current.map((p) => (p.id === id ? { ...p, ...safePatch } : p)),
        opts
      );
    },
    [setPasses]
  );

  const confirmDeletePass = useCallback(async () => {
    const id = deleteConfirmPassId;
    if (!id || deleteBusy) return;
    setDeleteBusy(true);
    const previous = passesRef.current;
    try {
      const next = previous.filter((p) => p.id !== id);
      passesRef.current = next;
      setLocalPasses(next);
      const json = JSON.stringify(next);
      lastEmittedCabPassesJsonRef.current = json;
      onCabPassesJsonChangeRef.current(json);
      if (onCommitCabPassesNowRef.current) {
        const ok = await onCommitCabPassesNowRef.current(json);
        if (ok === false) {
          passesRef.current = previous;
          setLocalPasses(previous);
          lastEmittedCabPassesJsonRef.current = JSON.stringify(previous);
          onCabPassesJsonChangeRef.current(JSON.stringify(previous));
          alert(translateUi("Could not delete the cab pass from Azure. Please try again."));
          return;
        }
      }
      setDeleteConfirmPassId(null);
    } catch (err) {
      console.error(err);
      passesRef.current = previous;
      setLocalPasses(previous);
      alert(translateUi("Could not delete the cab pass. Please try again."));
    } finally {
      setDeleteBusy(false);
    }
  }, [deleteBusy, deleteConfirmPassId]);

  const openAddPassColorPicker = useCallback(() => {
    setPendingPassColor(null);
    setAddPassColorOpen(true);
  }, []);

  const addPassWithColor = useCallback(
    (color: CabPassColor): string => {
      const id = newCabPassId();
      const current = passesRef.current;
      // Defer server persist until after we scroll to Valid from — immediate save used to
      // re-render the profile and cancel / undo the mobile scroll jump.
      flushSync(() => {
        setPasses(
          [
            ...current,
            {
              id,
              pass_color: color,
              pass_number: generateUniqueCabPassNumber(current),
              valid_from: "",
              valid_to: "",
              issued_by_name: "",
              issued_by_department: "",
              endorsements: "",
              issuer_signature_url: "",
              validity_scope: "",
            },
          ],
          { persist: "none" }
        );
        setExpandedPasses((prev) => ({ ...prev, [id]: true }));
        setReadonlyPasses((prev) => ({ ...prev, [id]: false }));
        setAddPassColorOpen(false);
        setPendingPassColor(null);
        setScrollToPassId(id);
      });
      // Persist after the first scroll attempts have started.
      window.setTimeout(() => {
        flushCabPassCommit();
      }, 900);
      return id;
    },
    [flushCabPassCommit, setPasses]
  );

  useLayoutEffect(() => {
    if (!scrollToPassId) return;
    const id = scrollToPassId;
    const compact = shouldUseCabPassCompactScroll(isMobileOrTablet);
    // Keep retrying independently of `passes` re-renders (those cancelled a single rAF before).
    let cancelled = false;

    const tryFocusFirstField = (): boolean => {
      if (cancelled) return false;
      // Prefer DOM query — card ref can lag one frame after flushSync expand.
      const hasTarget =
        Boolean(document.querySelector(`[data-cab-pass-valid-from-anchor="${id}"]`)) ||
        Boolean(findCabPassMobileField(id, "valid-from")) ||
        Boolean(document.querySelector(`[data-cab-pass-card="${id}"]`));
      if (!hasTarget) return false;
      mobileFieldFocusCleanupRef.current?.();
      mobileFieldFocusCleanupRef.current = focusCabPassValidFromField(id);
      return true;
    };

    const retryMs = compact
      ? [0, 32, 80, 160, 280, 450, 700, 1000, 1400, 2000, 2800, 3600]
      : [0, 50, 150];
    const timers = retryMs.map((ms) =>
      window.setTimeout(() => {
        tryFocusFirstField();
      }, ms)
    );
    const clearId = window.setTimeout(
      () => {
        if (!cancelled) setScrollToPassId(null);
      },
      (retryMs[retryMs.length - 1] ?? 0) + 100
    );

    return () => {
      cancelled = true;
      timers.forEach((t) => window.clearTimeout(t));
      window.clearTimeout(clearId);
    };
  }, [scrollToPassId, isMobileOrTablet]);

  useEffect(() => {
    return () => {
      mobileFieldFocusCleanupRef.current?.();
      mobileFieldFocusCleanupRef.current = null;
      fieldScrollLockCleanupRef.current?.();
      fieldScrollLockCleanupRef.current = null;
      horizontalPinCleanupRef.current?.();
      horizontalPinCleanupRef.current = null;
    };
  }, []);

  /**
   * On focus of cab-pass text fields: bring the field above the keyboard and pin X only.
   * Do not freeze vertical scroll — that trapped Issuer department under the keyboard.
   */
  const beginCabPassFieldScrollLock = useCallback((el: HTMLElement | null, passId: string) => {
    if (!el) return;
    if (!shouldUseCabPassCompactScroll(isMobileOrTablet)) return;
    setMobilePreviewUnlocked((prev) => ({ ...prev, [passId]: false }));
    mobileFieldFocusCleanupRef.current?.();
    mobileFieldFocusCleanupRef.current = null;
    fieldScrollLockCleanupRef.current?.();
    fieldScrollLockCleanupRef.current = ensureCabPassFieldVisibleAboveKeyboard(el, 1600);
    horizontalPinCleanupRef.current?.();
    horizontalPinCleanupRef.current = pinCabPassHorizontalScroll(8000);
  }, [isMobileOrTablet]);

  const endCabPassFieldScrollLock = useCallback(() => {
    fieldScrollLockCleanupRef.current?.();
    fieldScrollLockCleanupRef.current = null;
    // Keep horizontal pinned briefly after blur (department text can widen layout as preview updates).
    horizontalPinCleanupRef.current?.();
    horizontalPinCleanupRef.current = pinCabPassHorizontalScroll(1800);
    flushCabPassCommit();
  }, [flushCabPassCommit]);

  /** Value change on text fields — clamp X only. Never re-scroll on each keystroke. */
  const onCabPassTextFieldChanged = useCallback((_el?: HTMLElement | null) => {
    resetEmployeeProfileHorizontalScroll();
    horizontalPinCleanupRef.current?.();
    horizontalPinCleanupRef.current = pinCabPassHorizontalScroll(1200);
  }, []);

  useLayoutEffect(() => {
    if (passes.length === 0) return;
    resetEmployeeProfileHorizontalScroll();
  }, [passes.length]);

  const renewPass = useCallback(
    (id: string, validFrom: string, validTo: string) => {
      if (!cabPassRenewAllowed) {
        alert(translateUi(RENEW_FORBIDDEN_MSG));
        return;
      }
      if (!validFrom || !validTo) {
        alert(translateUi("Please enter both renewal dates."));
        return;
      }
      if (validFrom > validTo) {
        alert(translateUi("Valid from date must be on or before valid to date."));
        return;
      }
      updatePass(
        id,
        {
          valid_from: validFrom,
          valid_to: validTo,
          digital: true,
          verification_token: randomVerifyToken(),
          scan_code: randomCabPassScanCode(),
        },
        { persist: "immediate" }
      );
      setExpandedPasses((prev) => ({ ...prev, [id]: true }));
      setReadonlyPasses((prev) => ({ ...prev, [id]: false }));
    },
    [cabPassRenewAllowed, updatePass]
  );

  const revokePass = useCallback(
    (id: string) => {
      updatePass(
        id,
        { digital: false, verification_token: undefined, scan_code: undefined },
        { persist: "immediate" }
      );
    },
    [updatePass]
  );

  const togglePassExpanded = useCallback((id: string) => {
    setExpandedPasses((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const closeDigitalIssueModal = useCallback(() => {
    if (digitalIssueBusy) return;
    setDigitalIssuePassId(null);
    setDigitalIssueResult(null);
  }, [digitalIssueBusy]);

  const makeDigital = useCallback(
    async (passId: string) => {
      if (!cc) {
        setDigitalIssueResult({ ok: false, message: "Company code is required to create a digital pass." });
        return;
      }
      const employeeEmail = String(employee.email ?? "").trim();
      if (!employeeEmail || !employeeEmail.includes("@")) {
        setDigitalIssueResult({
          ok: false,
          message:
            "This employee has no email address on their record. Add an email under Main profile before issuing a digital pass.",
        });
        return;
      }
      setDigitalIssueBusy(true);
      setDigitalIssueResult(null);
      try {
        const headers: HeadersInit = { "Content-Type": "application/json" };
        if (cc) (headers as Record<string, string>)["X-Company-Code"] = cc;
        if (currentUserEmail) (headers as Record<string, string>)["X-User-Email"] = currentUserEmail;
        const actorName = String(currentUserFullName ?? "").trim();
        if (actorName && !actorName.includes("@")) {
          (headers as Record<string, string>)["X-User-Name"] = actorName;
        }
        const res = await fetch(
          `/api/employees/${encodeURIComponent(employee.id)}/cab-passes/${encodeURIComponent(passId)}/issue-digital`,
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              companyCode: cc,
              ...(actorName && !actorName.includes("@") ? { actorName } : {}),
            }),
          }
        );
        const data = (await res.json().catch(() => ({}))) as {
          error?: string;
          cab_passes_json?: string;
          emailed?: boolean;
          emailError?: string;
        };
        if (!res.ok) {
          setDigitalIssueResult({ ok: false, message: data.error || "Failed to issue digital pass." });
          return;
        }
        if (typeof data.cab_passes_json === "string") {
          lastEmittedCabPassesJsonRef.current = data.cab_passes_json;
          onCabPassesJsonChangeRef.current(data.cab_passes_json);
          const parsed = parseCabPassesJsonClient(data.cab_passes_json);
          passesRef.current = parsed;
          setLocalPasses(parsed);
        } else {
          // Fallback: mark digital locally if server omitted json.
          const token = randomVerifyToken();
          const scan_code = randomCabPassScanCode();
          setPasses(
            passesRef.current.map((p) =>
              p.id === passId ? { ...p, digital: true, verification_token: token, scan_code } : p
            ),
            { persist: "immediate" }
          );
        }
        if (data.emailed) {
          setDigitalIssueResult({
            ok: true,
            message: `Digital pass issued and emailed to ${employeeEmail}.`,
          });
        } else {
          setDigitalIssueResult({
            ok: true,
            message: `Digital pass issued.${
              data.emailError ? ` Email was not sent: ${data.emailError}` : " Email was not sent."
            }`,
          });
        }
        // Collapse the entry form so the issued pass shows as the compact preview card.
        setExpandedPasses((prev) => ({ ...prev, [passId]: false }));
        setMobilePreviewUnlocked((prev) => {
          if (!(passId in prev)) return prev;
          const next = { ...prev };
          delete next[passId];
          return next;
        });
      } catch (err) {
        console.error(err);
        setDigitalIssueResult({ ok: false, message: "Failed to issue digital pass." });
      } finally {
        setDigitalIssueBusy(false);
      }
    },
    [cc, currentUserEmail, currentUserFullName, employee.email, employee.id, setPasses]
  );

  const inputClass = cn(
    // text-base (16px) on phones: iOS zooms inputs under 16px and that pan is the container drift.
    // No overflow-hidden here: it clips focus:ring. Width is clamped with min-w-0/max-w-full.
    "w-full min-w-0 max-w-full box-border px-3 py-2 sm:px-3.5 sm:py-2.5 border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-colors text-base sm:text-sm",
    isDarkMode ? "bg-slate-800 border-slate-700 text-slate-100" : "bg-slate-50 border-slate-200 text-slate-900"
  );
  const labelClass = cn(
    "block text-xs font-semibold uppercase tracking-wider mb-2",
    isDarkMode ? "text-slate-300" : "text-slate-500"
  );

  return (
    <div
      data-cab-passes-tab
      className={cn(
        "rounded-2xl border p-4 sm:p-6 lg:p-8 shadow-sm min-w-0 max-w-full overflow-x-hidden overscroll-x-none space-y-6",
        isDarkMode ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white"
      )}
    >
      <div
        className={cn(
          "flex w-full min-w-0 gap-2",
          isMobileOrTablet ? "flex-col" : "sm:flex-row sm:items-start sm:justify-between sm:gap-4"
        )}
      >
        <div
          className={cn(
            "min-w-0 flex-1",
            !isMobileOrTablet && "sm:pr-2 md:pr-4"
          )}
        >
          <h2 className={cn("text-xl font-bold flex items-center gap-2", isDarkMode ? "text-slate-100" : "text-slate-900")}>
            <KeyRound className={cn("w-6 h-6 shrink-0", isDarkMode ? "text-amber-300" : "text-amber-700")} />
            {translateUi("Cab Passes")}
          </h2>
          <p className={cn("text-sm mt-1", isDarkMode ? "text-slate-400" : "text-slate-600")}>
            Record cab passes in standard driving cab pass layout. Digital passes show a QR code on the front in a panel
            the same size as the photo, stacked under it on the right; all holder and issue lines stay on the left.
          </p>
        </div>
        {mayEditCabPasses ? (
          <button
            type="button"
            onClick={openAddPassColorPicker}
            className={cn(
              "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors",
              isMobileOrTablet ? "w-full" : "w-full sm:w-auto",
              isDarkMode
                ? "border-indigo-500/40 bg-indigo-500/15 text-indigo-200 hover:bg-indigo-500/25"
                : "border-indigo-200 bg-indigo-50 text-indigo-900 hover:bg-indigo-100"
            )}
          >
            <Plus className="w-4 h-4" />
            {translateUi("Add new cab pass")}
          </button>
        ) : null}
      </div>

      {canInteractWithCabPassTab ? (
        <div
          className={cn(
            "rounded-xl border p-3 sm:p-4 text-xs leading-relaxed",
            isDarkMode ? "border-sky-500/25 bg-sky-950/25 text-slate-300" : "border-sky-200 bg-sky-50/70 text-slate-700"
          )}
          role="note"
          aria-label={translateUi("Cab pass permission summary")}
        >
          <div className="flex items-start gap-2.5">
            <Info className={cn("w-4 h-4 shrink-0 mt-0.5", isDarkMode ? "text-sky-400" : "text-sky-600")} />
            <div className="min-w-0 space-y-2">
              <p
                className={cn(
                  "font-bold text-[11px] uppercase tracking-wider",
                  isDarkMode ? "text-slate-200" : "text-slate-800"
                )}
              >
                {translateUi("Who can change cab passes")}
              </p>
              <ul className={cn("list-disc space-y-1.5 pl-4 marker:text-current", isDarkMode ? "text-slate-300" : "text-slate-600")}>
                <li>
                  <span className="font-semibold text-inherit">{translateUi("Company role permissions")}</span> (Administration → Role
                  permissions) can grant <span className="font-semibold"> {translateUi("Edit")} </span>, <span className="font-semibold"> {translateUi("Delete")} </span>,{" "}
                  <span className="font-semibold"> {translateUi("Renew")} </span>{translateUi(", or")} <span className="font-semibold">{translateUi("Revoke digital")}</span> for cab
                  passes. When assigned, those override the defaults below for your account.{" "}
                  <span className="font-semibold">{translateUi("Adding a new pass")}</span> needs{" "}
                  <span className="font-semibold">{translateUi("Edit cab passes")}</span> or company/system administrator — Renew, Delete, or Revoke
                  alone do not.
                </li>
                <li>
                  <span className="font-semibold text-inherit">{translateUi("Default rules")}</span> if granular permissions above are{" "}
                  <span className="font-semibold">not</span> ticked for your role:{" "}
                  <span className="font-semibold">{translateUi("Company administrators")}</span> control add, edit, delete, renew, and revoke
                  (while in date).                   <span className="font-semibold">{translateUi("Cab passes")}</span> (baseline view) plus matching{" "}
                  <span className="font-semibold">{translateUi("Assessing manager")}</span> lets the assessing manager delegate renew-only.
                  {!cabPassesManagePermission &&
                  !cabPassCompanyAdminActions &&
                  (cabPassPermissionEdit ||
                    cabPassPermissionDelete ||
                    cabPassPermissionRevoke ||
                    cabPassPermissionRenew) ? (
                    <span className="block mt-1.5 italic">
                      {translateUi('You rely on granular role permissions rather than baseline "Cab passes" view alone.')}
                    </span>
                  ) : null}
                </li>
                <li>
                  <span className="font-semibold text-inherit">{translateUi("Expired passes")}</span> cannot be edited in detail or have
                  digital verification revoked. Use{" "}
                  <span className="font-semibold"> {translateUi("Renew")} </span> {translateUi("if you're allowed, or someone with Delete / admin access.")}
                </li>
              </ul>
            </div>
          </div>
        </div>
      ) : null}

      <div
        className={cn(
          "rounded-xl border p-3 text-xs space-y-2",
          isDarkMode ? "border-slate-600 bg-slate-800/60 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-700"
        )}
      >
        <p className={cn("font-bold uppercase tracking-wider text-[10px]", isDarkMode ? "text-slate-400" : "text-slate-500")}>
          {translateUi("Pass colours")}
        </p>
        <ul className="space-y-1.5 list-none">
          <li className="flex gap-2 items-start">
            <span className="mt-0.5 h-3 w-3 rounded-full bg-emerald-500 shrink-0 border border-white/20" />
            <span className="flex min-w-0 flex-1 items-start">
              <span className={cn("w-[52px] shrink-0 font-semibold", isDarkMode ? "text-emerald-400" : "text-emerald-600")}>{translateUi("Green")}</span>
              <span className="w-[12px] shrink-0 text-center">-</span>
              <span className="min-w-0">{translateUi("Authorised to Assess.")}</span>
            </span>
          </li>
          <li className="flex gap-2 items-start">
            <span className="mt-0.5 h-3 w-3 rounded-full bg-amber-400 shrink-0 border border-slate-600/30" />
            <span className="flex min-w-0 flex-1 items-start">
              <span className={cn("w-[52px] shrink-0 font-semibold", isDarkMode ? "text-amber-300" : "text-amber-700")}>{translateUi("Yellow")}</span>
              <span className="w-[12px] shrink-0 text-center">-</span>
              <span className="min-w-0"> {translateUi("PTS (Personal Track Safety) competent.")} </span>
            </span>
          </li>
          <li className="flex gap-2 items-start">
            <span className="mt-0.5 h-3 w-3 rounded-full bg-sky-600 shrink-0 border border-white/20" />
            <span className="flex min-w-0 flex-1 items-start">
              <span className={cn("w-[52px] shrink-0 font-semibold", isDarkMode ? "text-sky-300" : "text-sky-700")}>{translateUi("Blue")}</span>
              <span className="w-[12px] shrink-0 text-center">-</span>
              <span className="min-w-0">{translateUi("Operational Rules competent.")}</span>
            </span>
          </li>
          <li className="flex gap-2 items-start">
            <span className="mt-0.5 h-3 w-3 rounded-full bg-red-600 shrink-0 border border-white/20" />
            <span className="flex min-w-0 flex-1 items-start">
              <span className={cn("w-[52px] shrink-0 font-semibold", isDarkMode ? "text-red-300" : "text-red-700")}>Red</span>
              <span className="w-[12px] shrink-0 text-center">-</span>
              <span className="min-w-0">{translateUi("Issued with endorsements; usually accompanied by a green cab pass holder.")}</span>
            </span>
          </li>
          <li className="flex gap-2 items-start">
            <span className="mt-0.5 h-3 w-3 rounded-full bg-slate-900 shrink-0 border border-slate-500" />
            <span className="flex min-w-0 flex-1 items-start">
              <span className={cn("w-[52px] shrink-0 font-semibold", isDarkMode ? "text-slate-200" : "text-slate-800")}>{translateUi("Black")}</span>
              <span className="w-[12px] shrink-0 text-center">-</span>
              <span className="min-w-0">{translateUi("Typically used for route learning.")}</span>
            </span>
          </li>
        </ul>
      </div>

      {passes.length === 0 && !canInteractWithCabPassTab ? (
        <p className={cn("text-sm", isDarkMode ? "text-slate-400" : "text-slate-500")}>{translateUi("No cab passes recorded.")}</p>
      ) : null}

      <div className="space-y-4 min-w-0 max-w-full overflow-x-hidden">
        {passes.map((pass) => {
          const passIsReadOnly = Boolean(readonlyPasses[pass.id]);
          const passExpired = cabPassIsExpired(pass.valid_to);
          const canEditThisPass = Boolean(mayEditCabPasses && canInteractWithCabPassTab && !passIsReadOnly && !passExpired);
          const expanded = Boolean(expandedPasses[pass.id]);
          const verifyUrl =
            pass.digital && pass.verification_token && cc
              ? buildCabPassVerifyUrl(origin, {
                  companyCode: cc,
                  employeeId: employee.id,
                  passId: pass.id,
                  token: pass.verification_token,
                  scanCode: pass.scan_code,
                })
              : "";
          const cabPassPaintKey = `${pass.issuer_signature_url ?? ""}|${employee.company_logo_url ?? ""}|g${cabPassPreviewGen}`;
          return (
            <div
              key={pass.id}
              data-cab-pass-card={pass.id}
              ref={(el) => {
                passCardElsRef.current[pass.id] = el;
              }}
              className={cn(
                "rounded-xl border p-3 sm:p-4 space-y-3 scroll-mt-24 min-w-0 max-w-full max-lg:overflow-x-hidden max-lg:overscroll-x-none [overflow-anchor:none]",
                isDarkMode ? "border-slate-600 bg-slate-800/40" : "border-slate-200 bg-slate-50/80"
              )}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  {expanded ? (
                    <>
                      <span
                        className={cn(
                          "h-3 w-3 rounded-full shrink-0 border border-black/10",
                          CAB_PASS_COLOR_OPTIONS.find((c) => c.value === pass.pass_color)?.swatch ?? "bg-slate-400"
                        )}
                      />
                      <span className={cn("text-sm font-semibold truncate", isDarkMode ? "text-slate-100" : "text-slate-900")}>
                        {String(pass.pass_number ?? "").trim() || "Cab pass"}
                      </span>
                    </>
                  ) : (
                    <span className={cn("text-xs font-semibold uppercase tracking-wide", isDarkMode ? "text-slate-300" : "text-slate-600")}>
                      {translateUi("Pass Issued Preview")}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => togglePassExpanded(pass.id)}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-xl border px-3 py-1.5 text-xs font-semibold shadow-sm transition-all duration-150 hover:-translate-y-[0.5px] focus-visible:outline-none focus-visible:ring-2",
                    isDarkMode
                      ? "border-slate-500 bg-slate-800/90 text-slate-100 hover:bg-slate-700 focus-visible:ring-slate-400/70"
                      : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100 focus-visible:ring-slate-300"
                  )}
                >
                  {expanded ? translateUi("Collapse") : translateUi("Expand")}
                </button>
              </div>
              {!expanded ? (
                <div className="flex w-full min-w-0 max-w-full flex-col items-stretch gap-3 overflow-x-hidden lg:flex-row">
                  <div className="min-w-0 w-full max-w-full shrink lg:w-[22rem]">
                    <CabPassScaledCardPreview
                      key={`cabpv-${pass.id}-strip`}
                      passId={pass.id}
                      className="!mx-0 w-full max-w-full lg:max-w-[22rem]"
                      paintKey={cabPassPaintKey}
                    >
                      <DrivingCabPassCard
                        passColor={pass.pass_color}
                        holderName={employee.full_name ?? ""}
                        holderTitle={employee.role ?? ""}
                        holderDept={cabPassDeptOnCard(pass)}
                        employeeId={employee.id ?? ""}
                        passNumber={pass.pass_number ?? ""}
                        validFrom={pass.valid_from ?? ""}
                        validTo={pass.valid_to ?? ""}
                        validityScope={pass.validity_scope}
                        issuedByName={pass.issued_by_name ?? ""}
                        issuedByDept={cabPassDeptOnCard(pass)}
                        endorsements={pass.endorsements ?? ""}
                        issuerSignatureUrl={pass.issuer_signature_url}
                        dateRangeOk={isPassDateRangeOk(pass.valid_from, pass.valid_to)}
                        companyName={employee.company_name}
                        companyAddress={employee.company_address}
                        companyLogoUrl={String(employee.company_logo_url ?? "").trim() || undefined}
                        holderPhotoUrl={
                          String(employee.image_url ?? "").trim() ||
                          String(pass.image_url ?? "").trim() ||
                          undefined
                        }
                        verifyUrl={verifyUrl || undefined}
                        forcePaperStyle
                      />
                    </CabPassScaledCardPreview>
                  </div>
                  <div className="min-w-0 w-full max-w-full shrink-0 lg:w-[11rem] lg:self-stretch">
                    {verifyUrl ? (
                      <div className="flex h-full min-h-[130px] w-full max-w-full flex-col items-center justify-center gap-1 rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
                        <QRCodeSVG
                          value={verifyUrl}
                          size={isMobileOrTablet ? 180 : 260}
                          level="Q"
                          includeMargin
                          className="h-auto w-full max-w-full"
                        />
                        <p className={cn("text-[10px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-500" : "text-slate-500")}>
                          {translateUi("Scan to verify")}
                        </p>
                      </div>
                    ) : (
                      <div
                        className={cn(
                          "flex h-full min-h-[130px] items-center justify-center rounded-lg border px-2 text-center text-[10px] font-semibold tracking-wide",
                          isDarkMode ? "border-slate-600 bg-slate-800 text-slate-400" : "border-slate-200 bg-white text-slate-500"
                        )}
                      >
                        <span className="max-w-[9rem]">{translateUi("QR code shown when pass is issued.")}</span>
                      </div>
                    )}
                  </div>
                  {canInteractWithCabPassTab ? (
                    <div className="shrink-0 lg:ml-auto lg:w-[11rem] lg:self-start">
                      <div className="flex flex-col gap-2">
                        {mayEditCabPasses ? (
                          <button
                            type="button"
                            disabled={passExpired}
                            onClick={() => {
                              if (passExpired) return;
                              setReadonlyPasses((prev) => ({ ...prev, [pass.id]: false }));
                              setExpandedPasses((prev) => ({ ...prev, [pass.id]: true }));
                            }}
                            className={cn(
                              "rounded-full border px-3.5 py-2 text-xs font-semibold shadow-sm transition-all duration-150 hover:-translate-y-[0.5px] focus-visible:outline-none focus-visible:ring-2 disabled:hover:translate-y-0 disabled:cursor-not-allowed disabled:opacity-45",
                              isDarkMode
                                ? "border-slate-500/70 bg-slate-700/35 text-slate-100 hover:bg-slate-700/55 focus-visible:ring-slate-400/70"
                                : "border-slate-300 bg-slate-100 text-slate-800 hover:bg-slate-200 focus-visible:ring-slate-300"
                            )}
                          > {translateUi("Edit")} </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => {
                            setReadonlyPasses((prev) => ({ ...prev, [pass.id]: true }));
                            setExpandedPasses((prev) => ({ ...prev, [pass.id]: true }));
                          }}
                          className={cn(
                            "rounded-full border px-3.5 py-2 text-xs font-semibold shadow-sm transition-all duration-150 hover:-translate-y-[0.5px] focus-visible:outline-none focus-visible:ring-2",
                            isDarkMode
                              ? "border-slate-500/70 bg-slate-700/25 text-slate-200 hover:bg-slate-700/45 focus-visible:ring-slate-400/70"
                              : "border-slate-300 bg-slate-50 text-slate-700 hover:bg-slate-100 focus-visible:ring-slate-300"
                          )}
                        > {translateUi("View")} </button>
                        <button
                          type="button"
                          onClick={() => openRenewModal(pass.id, pass)}
                          className={cn(
                            "rounded-full border px-3.5 py-2 text-xs font-semibold shadow-sm transition-all duration-150 hover:-translate-y-[0.5px] focus-visible:outline-none focus-visible:ring-2",
                            isDarkMode
                              ? "border-emerald-500/45 bg-emerald-500/18 text-emerald-100 hover:bg-emerald-500/30 focus-visible:ring-emerald-400/60"
                              : "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 focus-visible:ring-emerald-200"
                          )}
                        > {translateUi("Renew")} </button>
                        {mayRevokeCabPasses ? (
                          <button
                            type="button"
                            onClick={() => revokePass(pass.id)}
                            disabled={!pass.digital || passExpired}
                            className={cn(
                              "rounded-full border px-3.5 py-2 text-xs font-semibold shadow-sm transition-all duration-150 hover:-translate-y-[0.5px] focus-visible:outline-none focus-visible:ring-2 disabled:hover:translate-y-0 disabled:cursor-not-allowed disabled:opacity-45",
                              isDarkMode
                                ? "border-amber-500/45 bg-amber-500/18 text-amber-100 hover:bg-amber-500/30 focus-visible:ring-amber-400/60"
                                : "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 focus-visible:ring-amber-200"
                            )}
                          > {translateUi("Revoke")} </button>
                        ) : null}
                        {mayDeleteCabPasses ? (
                          <button
                            type="button"
                            onClick={() => setDeleteConfirmPassId(pass.id)}
                            className={cn(
                              "rounded-full border px-3.5 py-2 text-xs font-semibold shadow-sm transition-all duration-150 hover:-translate-y-[0.5px] focus-visible:outline-none focus-visible:ring-2",
                              isDarkMode
                                ? "border-rose-500/45 bg-rose-500/18 text-rose-100 hover:bg-rose-500/30 focus-visible:ring-rose-400/60"
                                : "border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100 focus-visible:ring-rose-200"
                            )}
                          > {translateUi("Delete")} </button>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {expanded ? (
                <div
                  className="min-w-0 max-w-full space-y-4 max-lg:overflow-x-hidden max-lg:overscroll-x-none"
                  onFocusCapture={(e) => {
                    const t = e.target;
                    if (!(t instanceof HTMLElement)) return;
                    const tag = t.tagName;
                    if (tag !== "INPUT" && tag !== "TEXTAREA" && tag !== "SELECT") return;
                    beginCabPassFieldScrollLock(t, pass.id);
                  }}
                  onBlurCapture={(e) => {
                    const next = e.relatedTarget;
                    if (next instanceof Node && e.currentTarget.contains(next)) return;
                    endCabPassFieldScrollLock();
                  }}
                >
              {passIsReadOnly ? (
                <p className={cn("text-xs font-semibold uppercase tracking-wide", isDarkMode ? "text-amber-300" : "text-amber-700")}>
                  {translateUi("View mode (read-only)")}
                </p>
              ) : null}
              <div>
                <label className={labelClass}>{translateUi("Pass colour")}</label>
                {canEditThisPass ? (
                  <select
                    value={pass.pass_color}
                    onChange={(e) => {
                      updatePass(pass.id, { pass_color: e.target.value as CabPassColor }, { persist: "immediate" });
                      const passId = pass.id;
                      if (!shouldUseCabPassCompactScroll(isMobileOrTablet)) return;
                      const run = () => {
                        mobileFieldFocusCleanupRef.current?.();
                        mobileFieldFocusCleanupRef.current = focusCabPassValidFromField(passId);
                      };
                      [0, 50, 150, 350, 600, 1000].forEach((ms) => window.setTimeout(run, ms));
                    }}
                    className={inputClass}
                    autoComplete="off"
                  >
                    {CAB_PASS_COLOR_OPTIONS.map((c) => (
                      <option key={c.value} value={c.value}>
                        {translateUi(c.label)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className={cn("text-sm capitalize", isDarkMode ? "text-slate-200" : "text-slate-800")}>
                    {translateUi(CAB_PASS_COLOR_OPTIONS.find((c) => c.value === pass.pass_color)?.label ?? pass.pass_color)}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0 max-w-full max-lg:overflow-x-hidden">
                <div className="min-w-0 max-w-full">
                  <label className={labelClass}>{translateUi("Cab pass number")}</label>
                  <p
                    className={cn(
                      "text-sm font-mono tabular-nums",
                      isDarkMode ? "text-slate-200" : "text-slate-800"
                    )}
                    title={translateUi("Assigned automatically and cannot be edited")}
                  >
                    {pass.pass_number || "—"}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2 min-w-0 max-w-full">
                  <div data-cab-pass-valid-from-anchor={pass.id} className="scroll-mt-28 min-w-0 max-w-full" data-cab-pass-field-block>
                    <label className={labelClass}>{translateUi("Valid from")}</label>
                    {canEditThisPass ? (
                      <input
                        type="date"
                        data-cab-pass-first-field={pass.id}
                        data-cab-pass-mobile-field={`${pass.id}:valid-from`}
                        value={pass.valid_from}
                        onChange={(e) => updatePass(pass.id, { valid_from: e.target.value }, { persist: "none" })}
                        onBlur={() => flushCabPassCommit()}
                        onKeyDown={(e) => {
                          if (e.key !== "Enter") return;
                          e.preventDefault();
                          mobileFieldFocusCleanupRef.current?.();
                          mobileFieldFocusCleanupRef.current = focusNextCabPassMobileField(pass.id, "valid-from");
                        }}
                        className={cn(inputClass, "scroll-mt-28")}
                        autoComplete="off"
                      />
                    ) : (
                      <p className={cn("text-sm", isDarkMode ? "text-slate-200" : "text-slate-800")}>
                        {pass.valid_from ? formatDateUK(pass.valid_from) : "—"}
                      </p>
                    )}
                  </div>
                  <div className="min-w-0 max-w-full" data-cab-pass-field-block>
                    <label className={labelClass}>{translateUi("Valid to")}</label>
                    {canEditThisPass ? (
                      <input
                        type="date"
                        data-cab-pass-mobile-field={`${pass.id}:valid-to`}
                        value={pass.valid_to}
                        onChange={(e) => updatePass(pass.id, { valid_to: e.target.value }, { persist: "none" })}
                        onBlur={() => flushCabPassCommit()}
                        onKeyDown={(e) => {
                          if (e.key !== "Enter") return;
                          e.preventDefault();
                          mobileFieldFocusCleanupRef.current?.();
                          mobileFieldFocusCleanupRef.current = focusNextCabPassMobileField(pass.id, "valid-to");
                        }}
                        className={cn(inputClass, "scroll-mt-28")}
                        autoComplete="off"
                      />
                    ) : (
                      <p className={cn("text-sm", isDarkMode ? "text-slate-200" : "text-slate-800")}>
                        {pass.valid_to ? formatDateUK(pass.valid_to) : "—"}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0 max-w-full max-lg:overflow-x-hidden max-lg:overscroll-x-none">
                <div className="min-w-0 max-w-full" data-cab-pass-field-block>
                  <label className={labelClass}>{translateUi("Issued by (name)")}</label>
                  {canEditThisPass ? (
                    <input
                      type="text"
                      name={`cab-pass-issued-by-name-${pass.id}`}
                      data-cab-pass-mobile-field={`${pass.id}:issued-by-name`}
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="words"
                      spellCheck={false}
                      value={pass.issued_by_name}
                      onFocus={(e) => beginCabPassFieldScrollLock(e.currentTarget, pass.id)}
                      onBlur={() => endCabPassFieldScrollLock()}
                      onChange={(e) => {
                        // Autofill must never auto-scroll — only update value + clamp horizontal overflow.
                        updatePass(pass.id, { issued_by_name: e.target.value }, { persist: "none" });
                        onCabPassTextFieldChanged(e.currentTarget);
                      }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key !== "Enter") return;
                        e.preventDefault();
                        fieldScrollLockCleanupRef.current?.();
                        fieldScrollLockCleanupRef.current = null;
                        mobileFieldFocusCleanupRef.current?.();
                        mobileFieldFocusCleanupRef.current = focusNextCabPassMobileField(
                          pass.id,
                          "issued-by-name",
                          { holdMs: 2200 }
                        );
                      }}
                      className={cn(inputClass, "min-w-0 max-w-full")}
                      placeholder="Name"
                    />
                  ) : (
                    <p className={cn("text-sm break-words", isDarkMode ? "text-slate-200" : "text-slate-800")}>{pass.issued_by_name || "—"}</p>
                  )}
                </div>
                <div className="min-w-0 max-w-full" data-cab-pass-field-block>
                  <label className={labelClass}>{translateUi("Issuer department (on pass)")}</label>
                  {canEditThisPass ? (
                    <input
                      type="text"
                      name={`cab-pass-issued-by-department-${pass.id}`}
                      data-cab-pass-issuer-department={pass.id}
                      data-cab-pass-mobile-field={`${pass.id}:issuer-department`}
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                      value={pass.issued_by_department}
                      onFocus={(e) => beginCabPassFieldScrollLock(e.currentTarget, pass.id)}
                      onChange={(e) => {
                        updatePass(pass.id, { issued_by_department: e.target.value }, { persist: "none" });
                        onCabPassTextFieldChanged(e.currentTarget);
                      }}
                      onBlur={() => endCabPassFieldScrollLock()}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key !== "Enter") return;
                        e.preventDefault();
                        fieldScrollLockCleanupRef.current?.();
                        fieldScrollLockCleanupRef.current = null;
                        // One step only: Issuer department → Validity (never skip to preview).
                        mobileFieldFocusCleanupRef.current?.();
                        mobileFieldFocusCleanupRef.current = focusNextCabPassMobileField(
                          pass.id,
                          "issuer-department",
                          { holdMs: 2200 }
                        );
                      }}
                      className={cn(inputClass, "min-w-0 max-w-full")}
                      placeholder={translateUi("Department")}
                    />
                  ) : (
                    <p className={cn("text-sm break-words", isDarkMode ? "text-slate-200" : "text-slate-800")}>{pass.issued_by_department || "—"}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0 max-w-full max-lg:overflow-x-hidden max-lg:overscroll-x-none">
                <div className="min-w-0 max-w-full" data-cab-pass-field-block>
                  <label className={labelClass}>{translateUi("Validity (routes / scope)")}</label>
                  {canEditThisPass ? (
                    <textarea
                      name={`cab-pass-validity-scope-${pass.id}`}
                      data-cab-pass-mobile-field={`${pass.id}:validity-scope`}
                      autoComplete="off"
                      value={pass.validity_scope ?? ""}
                      onFocus={(e) => beginCabPassFieldScrollLock(e.currentTarget, pass.id)}
                      onChange={(e) => {
                        updatePass(pass.id, { validity_scope: e.target.value }, { persist: "none" });
                        onCabPassTextFieldChanged(e.currentTarget);
                      }}
                      onBlur={() => endCabPassFieldScrollLock()}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key !== "Enter" || e.shiftKey) return;
                        e.preventDefault();
                        fieldScrollLockCleanupRef.current?.();
                        fieldScrollLockCleanupRef.current = null;
                        mobileFieldFocusCleanupRef.current?.();
                        mobileFieldFocusCleanupRef.current = focusNextCabPassMobileField(
                          pass.id,
                          "validity-scope"
                        );
                      }}
                      spellCheck={true}
                      className={cn(inputClass, "min-h-[72px] min-w-0 max-w-full resize-y whitespace-pre-wrap break-words scroll-mt-28")}
                      rows={2}
                      placeholder="e.g. All mainline routes (shown on pass as VALIDITY)"
                    />
                  ) : (
                    <p className={cn("text-sm whitespace-pre-wrap break-words", isDarkMode ? "text-slate-200" : "text-slate-800")}>
                      {pass.validity_scope != null && String(pass.validity_scope).length > 0 ? pass.validity_scope : "—"}
                    </p>
                  )}
                </div>

                <div className="min-w-0 max-w-full" data-cab-pass-field-block>
                  <label className={labelClass}>{translateUi("Endorsements (if any)")}</label>
                  {canEditThisPass ? (
                    <textarea
                      name={`cab-pass-endorsements-${pass.id}`}
                      data-cab-pass-mobile-field={`${pass.id}:endorsements`}
                      autoComplete="off"
                      value={pass.endorsements}
                      onFocus={(e) => beginCabPassFieldScrollLock(e.currentTarget, pass.id)}
                      onChange={(e) => {
                        updatePass(pass.id, { endorsements: e.target.value }, { persist: "none" });
                        onCabPassTextFieldChanged(e.currentTarget);
                      }}
                      onBlur={() => endCabPassFieldScrollLock()}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key !== "Enter" || e.shiftKey) return;
                        e.preventDefault();
                        fieldScrollLockCleanupRef.current?.();
                        fieldScrollLockCleanupRef.current = null;
                        // Reveal licence preview only when deliberately advancing from endorsements.
                        setMobilePreviewUnlocked((prev) => ({ ...prev, [pass.id]: true }));
                        window.setTimeout(() => {
                          mobileFieldFocusCleanupRef.current?.();
                          mobileFieldFocusCleanupRef.current = focusNextCabPassMobileField(
                            pass.id,
                            "endorsements"
                          );
                        }, 50);
                      }}
                      spellCheck={true}
                      className={cn(inputClass, "min-h-[72px] min-w-0 max-w-full resize-y whitespace-pre-wrap break-words scroll-mt-28")}
                      rows={2}
                      placeholder="Optional endorsements or conditions"
                    />
                  ) : (
                    <p className={cn("text-sm whitespace-pre-wrap break-words", isDarkMode ? "text-slate-200" : "text-slate-800")}>
                      {pass.endorsements || "—"}
                    </p>
                  )}
                </div>
              </div>

              <div
                className="w-full min-w-0 max-w-full sm:max-w-[50%]"
                onFocusCapture={() => {
                  if (shouldUseCabPassCompactScroll(isMobileOrTablet)) {
                    setMobilePreviewUnlocked((prev) => ({ ...prev, [pass.id]: true }));
                    horizontalPinCleanupRef.current?.();
                    horizontalPinCleanupRef.current = pinCabPassHorizontalScroll(2000);
                  }
                }}
              >
                <label className={labelClass}>{translateUi("Issuer signature")}</label>
                <SignaturePad
                  value={pass.issuer_signature_url}
                  canEdit={canEditThisPass}
                  isDarkMode={isDarkMode}
                  onChange={(next) => updatePass(pass.id, { issuer_signature_url: next }, { persist: "immediate" })}
                  onApplied={() => {
                    setCabPassPreviewGen((g) => g + 1);
                    horizontalPinCleanupRef.current?.();
                    horizontalPinCleanupRef.current = pinCabPassHorizontalScroll(2400);
                  }}
                />
              </div>

              <div>
                <label className={labelClass}>{translateUi("Pass image")}</label>
                {pass.image_url ? (
                  <div className="inline-block max-w-full">
                    <img
                      src={pass.image_url}
                      alt={`Cab pass ${pass.pass_number || pass.id}`}
                      className={cn("max-h-40 rounded-lg border object-contain", isDarkMode ? "border-slate-600" : "border-slate-200")}
                    />
                  </div>
                ) : null}
              </div>

              {(() => {
                const compact = shouldUseCabPassCompactScroll(isMobileOrTablet);
                const showLivePreview = !compact || !canEditThisPass || Boolean(mobilePreviewUnlocked[pass.id]);
                if (!showLivePreview) {
                  return (
                    <div
                      data-cab-pass-mobile-field={`${pass.id}:preview`}
                      className={cn(
                        "rounded-lg border border-dashed p-3 text-xs [overflow-anchor:none]",
                        isDarkMode ? "border-slate-600 text-slate-400" : "border-slate-300 text-slate-500"
                      )}
                    >
                      <p className="font-semibold">{translateUi("Pass preview")}</p>
                      <p className="mt-1">
                        {translateUi("Preview stays hidden while you fill the fields above, so the page won’t jump to it. It appears when you reach the signature or press Enter on Endorsements.")}
                      </p>
                      <button
                        type="button"
                        className={cn(
                          "mt-2 rounded-lg border px-2.5 py-1.5 text-xs font-semibold",
                          isDarkMode
                            ? "border-slate-500 text-slate-200 hover:bg-slate-800"
                            : "border-slate-300 text-slate-700 hover:bg-slate-50"
                        )}
                        onClick={() => {
                          setMobilePreviewUnlocked((prev) => ({ ...prev, [pass.id]: true }));
                          window.setTimeout(() => {
                            const preview = findCabPassMobileField(pass.id, "preview");
                            if (preview) {
                              mobileFieldFocusCleanupRef.current?.();
                              mobileFieldFocusCleanupRef.current = focusCabPassFieldMobileSticky(preview, {
                                offset: 96,
                                holdMs: 1200,
                                focus: false,
                              });
                            }
                          }, 50);
                        }}
                      >
                        {translateUi("Show pass preview")}
                      </button>
                    </div>
                  );
                }
                return null;
              })()}

              {( !shouldUseCabPassCompactScroll(isMobileOrTablet) ||
                !canEditThisPass ||
                Boolean(mobilePreviewUnlocked[pass.id]) ) ? (
              <div className="grid grid-cols-1 gap-3 xl:grid-cols-2 xl:items-start">
                <div
                  data-cab-pass-mobile-field={`${pass.id}:preview`}
                  className={cn(
                    "rounded-lg border p-3 sm:p-4 space-y-3 min-w-0 max-w-full [overflow-anchor:none]",
                    isDarkMode ? "border-slate-600 bg-slate-900/50" : "border-slate-200 bg-white"
                  )}
                >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className={cn("text-xs font-bold uppercase tracking-wider", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                    {translateUi("Pass layout & print")}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      printCabPassCreditCard(
                        pass.id,
                        `Digital Cab Pass ${fileSafeIssuedDate(pass.valid_from)}.pdf`
                      );
                    }}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors",
                      isDarkMode
                        ? "border-slate-500 bg-slate-800 text-slate-200 hover:bg-slate-700"
                        : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                    )}
                  >
                    <Printer className="w-3.5 h-3.5" />
                    {translateUi("Print credit-card size")}
                  </button>
                </div>
                <p className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-slate-600")}>
                  Landscape ISO ID-1 layout (85.6&nbsp;×&nbsp;53.98&nbsp;mm). Tap the card to flip — conditions of use
                  are on the reverse. <span className="font-semibold">{translateUi("Print credit-card size")}</span> opens a
                  two-page A4 sheet (front and reverse) with cut guides — use double-sided print, flip on long edge.
                </p>
                <div className="min-w-0 w-full max-w-full">
                <CabPassScaledCardPreview
                  key={`cabpv-${pass.id}-detail`}
                  passId={pass.id}
                  paintKey={cabPassPaintKey}
                >
                  <DrivingCabPassCard
                    passColor={pass.pass_color}
                    holderName={employee.full_name ?? ""}
                    holderTitle={employee.role ?? ""}
                    holderDept={cabPassDeptOnCard(pass)}
                    employeeId={employee.id ?? ""}
                    passNumber={pass.pass_number ?? ""}
                    validFrom={pass.valid_from ?? ""}
                    validTo={pass.valid_to ?? ""}
                    validityScope={pass.validity_scope}
                    issuedByName={pass.issued_by_name ?? ""}
                    issuedByDept={cabPassDeptOnCard(pass)}
                    endorsements={pass.endorsements ?? ""}
                    issuerSignatureUrl={pass.issuer_signature_url}
                    dateRangeOk={isPassDateRangeOk(pass.valid_from, pass.valid_to)}
                    companyName={employee.company_name}
                    companyAddress={employee.company_address}
                    companyLogoUrl={String(employee.company_logo_url ?? "").trim() || undefined}
                    holderPhotoUrl={
                      String(employee.image_url ?? "").trim() ||
                      String(pass.image_url ?? "").trim() ||
                      undefined
                    }
                    verifyUrl={verifyUrl || undefined}
                    forcePaperStyle
                  />
                </CabPassScaledCardPreview>
                </div>
                <p
                  className={cn(
                    "cab-pass-preview-hint mt-2 max-w-[22rem] mx-auto text-center text-[10px]",
                    isDarkMode ? "text-slate-500" : "text-slate-500"
                  )}
                >
                  Tap the card to flip — front shows all lines on the left, photo and verification QR in matching panels on
                  the right; reverse shows conditions of use.
                </p>
                </div>

                <div
                  className={cn(
                    "rounded-lg border p-3 space-y-3",
                    isDarkMode ? "border-slate-600 bg-slate-900/50" : "border-slate-200 bg-white"
                  )}
                >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className={cn("text-xs font-bold uppercase tracking-wider", isDarkMode ? "text-slate-400" : "text-slate-500")}>
                    {translateUi("Digital verification")}
                  </p>
                  {canEditThisPass && !pass.digital && onCommitCabPassesNow ? (
                    <button
                      type="button"
                      onClick={() => {
                        setDigitalIssueResult(null);
                        setDigitalIssuePassId(pass.id);
                      }}
                      className={cn(
                        "rounded-lg px-3 py-1.5 text-xs font-semibold border transition-colors w-full sm:w-auto",
                        isDarkMode
                          ? "border-indigo-500/50 bg-indigo-500/15 text-indigo-200 hover:bg-indigo-500/25"
                          : "border-indigo-200 bg-indigo-50 text-indigo-900 hover:bg-indigo-100"
                      )}
                    >
                      {translateUi("Create & Issue Digital Pass")}
                    </button>
                  ) : null}
                </div>
                {canEditThisPass && !pass.digital && onCommitCabPassesNow ? (
                  <p className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-slate-600")}>
                    Creating a digital pass saves it to Azure and emails a copy of the pass to the employee
                    {String(employee.email ?? "").trim() ? (
                      <>
                        {" "}
                        (<span className="font-semibold">{String(employee.email).trim()}</span>)
                      </>
                    ) : (
                      <> once an email address is on their Main profile</>
                    )}
                    .
                  </p>
                ) : null}
                {pass.digital && pass.verification_token && verifyUrl ? (
                  <>
                    {(() => {
                      const creatorLabel = digitalPassCreatorLabel(pass, currentUserEmail, currentUserFullName);
                      if (!creatorLabel && !pass.digital_created_at) return null;
                      return (
                      <p className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-slate-600")}>
                        Digital pass created
                        {creatorLabel ? (
                          <>
                            {" "}
                            by <span className="font-semibold">{creatorLabel}</span>
                          </>
                        ) : null}
                        {pass.digital_created_at ? (
                          <>
                            {" "}
                            on <span className="font-semibold">{formatDateTimeUK(pass.digital_created_at)}</span>
                          </>
                        ) : null}
                        .
                      </p>
                      );
                    })()}
                    <p className={cn("text-xs", isDarkMode ? "text-slate-400" : "text-slate-600")}>
                      Scan the QR code with your phone or tablet camera to open this driving cab pass in the browser (no
                      sign-in). The same QR appears beside the photo on the front under <span className="font-semibold">{translateUi("Pass layout & print")}</span>{" "}
                      above.
                    </p>
                    <div className="flex flex-col items-center gap-2">
                      <p className={cn("text-[10px] font-semibold uppercase tracking-wider", isDarkMode ? "text-slate-500" : "text-slate-500")}>
                        {translateUi("Scan to verify")}
                      </p>
                      <div className="rounded-xl bg-white p-3 border-2 border-slate-200 shadow-md">
                        <QRCodeSVG value={verifyUrl} size={200} level="Q" includeMargin />
                      </div>
                    </div>
                    <p className={cn("text-[10px] break-all font-mono text-center opacity-80", isDarkMode ? "text-slate-500" : "text-slate-500")}>
                      {verifyUrl}
                    </p>
                  </>
                ) : (
                  <p className={cn("text-xs", isDarkMode ? "text-slate-500" : "text-slate-500")}>
                    {canEditThisPass
                      ? "Create a digital pass to generate a QR code that shows this cab pass card when scanned."
                      : "No digital pass on this record."}
                  </p>
                )}
                </div>
              </div>
              ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {commitsCabPassesToServer ? (
        <p className={cn("text-xs", isDarkMode ? "text-slate-500" : "text-slate-500")}>
          <span className="font-semibold">{translateUi("Create & Issue Digital Pass")}</span> saves immediately so the QR code works
          right away, and emails a copy to the employee after you confirm. Other cab pass changes are also saved on
          this tab.
        </p>
      ) : null}
      {digitalIssuePassId ? (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-3 sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="issue-digital-cab-pass-title"
          onClick={() => {
            closeDigitalIssueModal();
          }}
        >
          <div
            className={cn(
              "relative w-full max-w-md rounded-t-2xl sm:rounded-xl border p-4 sm:p-5 shadow-xl max-h-[90vh] overflow-y-auto",
              isDarkMode ? "border-slate-600 bg-slate-900" : "border-slate-200 bg-white"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <GbrModalBackdrop />
            {digitalIssueResult ? (
              <>
                <h3
                  id="issue-digital-cab-pass-title"
                  className={cn(
                    "relative z-10 text-base sm:text-sm font-bold",
                    isDarkMode ? "text-slate-100" : "text-slate-900"
                  )}
                >
                  {digitalIssueResult.ok ? translateUi("Digital pass issued") : translateUi("Could not issue digital pass")}
                </h3>
                <div
                  className={cn(
                    "relative z-10 mt-3 rounded-xl border px-3 py-2.5 text-sm sm:text-xs leading-relaxed",
                    digitalIssueResult.ok
                      ? isDarkMode
                        ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-100"
                        : "border-emerald-200 bg-emerald-50 text-emerald-950"
                      : isDarkMode
                        ? "border-rose-500/30 bg-rose-500/10 text-rose-100"
                        : "border-rose-200 bg-rose-50 text-rose-950"
                  )}
                >
                  {digitalIssueResult.message}
                </div>
                <div className="relative z-10 mt-4 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
                  <button
                    type="button"
                    onClick={closeDigitalIssueModal}
                    className={cn(
                      "w-full sm:w-auto rounded-lg border px-3 py-2.5 sm:py-1.5 text-sm sm:text-xs font-semibold",
                      isDarkMode
                        ? "border-indigo-500/40 bg-indigo-500/15 text-indigo-200 hover:bg-indigo-500/25"
                        : "border-indigo-200 bg-indigo-50 text-indigo-900 hover:bg-indigo-100"
                    )}
                  > {translateUi("Done")} </button>
                </div>
              </>
            ) : (
              <>
                <h3
                  id="issue-digital-cab-pass-title"
                  className={cn(
                    "relative z-10 text-base sm:text-sm font-bold",
                    isDarkMode ? "text-slate-100" : "text-slate-900"
                  )}
                >
                  {translateUi("Issue digital pass and send email?")}
                </h3>
                <p
                  className={cn(
                    "relative z-10 mt-2 text-sm sm:text-xs leading-relaxed",
                    isDarkMode ? "text-slate-300" : "text-slate-600"
                  )}
                >
                  This will create a digital cab pass with QR verification, and email the front and reverse of the pass to the employee
                  {String(employee.email ?? "").trim() ? (
                    <>
                      {" "}
                      at <span className="font-semibold">{String(employee.email).trim()}</span>
                    </>
                  ) : (
                    <> — but this employee has no email address on their record</>
                  )}
                  .
                </p>
                <div
                  className={cn(
                    "relative z-10 mt-3 rounded-xl border px-3 py-2.5 text-sm sm:text-xs",
                    isDarkMode
                      ? "border-indigo-500/30 bg-indigo-500/10 text-indigo-100"
                      : "border-indigo-100 bg-indigo-50 text-indigo-950"
                  )}
                >
                  <p className="font-semibold">{translateUi("What happens next")}</p>
                  <ul className="mt-1.5 list-disc space-y-1 pl-4">
                    <li>{translateUi("Pass is saved digitally for public QR verification")}</li>
                    <li>{translateUi("Employee receives an email with pass details and a link to view the digital pass")}</li>
                    <li>{translateUi("You can still print the credit-card layout from this tab")}</li>
                  </ul>
                </div>
                {!String(employee.email ?? "").trim() ? (
                  <p
                    className={cn(
                      "relative z-10 mt-3 text-sm sm:text-xs font-semibold",
                      isDarkMode ? "text-amber-300" : "text-amber-800"
                    )}
                  >
                    {translateUi("Add an email address on the employee Main profile before issuing.")}
                  </p>
                ) : null}
                <div className="relative z-10 mt-4 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
                  <button
                    type="button"
                    disabled={digitalIssueBusy}
                    onClick={closeDigitalIssueModal}
                    className={cn(
                      "w-full sm:w-auto rounded-lg border px-3 py-2.5 sm:py-1.5 text-sm sm:text-xs font-semibold disabled:opacity-50",
                      isDarkMode
                        ? "border-slate-500 text-slate-200 hover:bg-slate-800"
                        : "border-slate-300 text-slate-700 hover:bg-slate-50"
                    )}
                  > {translateUi("Cancel")} </button>
                  <button
                    type="button"
                    disabled={
                      digitalIssueBusy ||
                      !String(employee.email ?? "").trim() ||
                      !String(employee.email ?? "").includes("@")
                    }
                    onClick={() => {
                      if (!digitalIssuePassId) return;
                      void makeDigital(digitalIssuePassId);
                    }}
                    className={cn(
                      "w-full sm:w-auto rounded-lg border px-3 py-2.5 sm:py-1.5 text-sm sm:text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed",
                      isDarkMode
                        ? "border-indigo-500/40 bg-indigo-500/15 text-indigo-200 hover:bg-indigo-500/25"
                        : "border-indigo-200 bg-indigo-50 text-indigo-900 hover:bg-indigo-100"
                    )}
                  >
                    {digitalIssueBusy ? translateUi("Issuing…") : translateUi("Issue and send email")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}
      {deleteConfirmPassId ? (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-3 sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-cab-pass-title"
          onClick={() => {
            if (!deleteBusy) setDeleteConfirmPassId(null);
          }}
        >
          <div
            className={cn(
              "relative w-full max-w-md rounded-t-2xl sm:rounded-xl border p-4 sm:p-5 shadow-xl",
              isDarkMode ? "border-slate-600 bg-slate-900" : "border-slate-200 bg-white"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <GbrModalBackdrop />
            <h3
              id="delete-cab-pass-title"
              className={cn(
                "relative z-10 text-base sm:text-sm font-bold",
                isDarkMode ? "text-slate-100" : "text-slate-900"
              )}
            >
              {translateUi("Delete cab pass?")}
            </h3>
            <p
              className={cn(
                "relative z-10 mt-2 text-sm sm:text-xs leading-relaxed",
                isDarkMode ? "text-slate-300" : "text-slate-600"
              )}
            >
              This permanently removes the pass
              {(() => {
                const p = passes.find((x) => x.id === deleteConfirmPassId);
                const num = String(p?.pass_number ?? "").trim();
                return num ? (
                  <>
                    {" "}
                    <span className="font-semibold">{num}</span>
                  </>
                ) : null;
              })()}{" "}
              from the database, including any digital QR verification index and related files for this pass. This
              cannot be undone.
            </p>
            <div className="relative z-10 mt-4 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
              <button
                type="button"
                disabled={deleteBusy}
                onClick={() => setDeleteConfirmPassId(null)}
                className={cn(
                  "w-full sm:w-auto rounded-lg border px-3 py-2.5 sm:py-1.5 text-sm sm:text-xs font-semibold disabled:opacity-50",
                  isDarkMode
                    ? "border-slate-500 text-slate-200 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-50"
                )}
              > {translateUi("Cancel")} </button>
              <button
                type="button"
                disabled={deleteBusy}
                onClick={() => void confirmDeletePass()}
                className={cn(
                  "w-full sm:w-auto rounded-lg border px-3 py-2.5 sm:py-1.5 text-sm sm:text-xs font-semibold disabled:opacity-50",
                  isDarkMode
                    ? "border-rose-500/50 bg-rose-500/20 text-rose-100 hover:bg-rose-500/30"
                    : "border-rose-300 bg-rose-600 text-white hover:bg-rose-700"
                )}
              >
                {deleteBusy ? translateUi("Deleting…") : translateUi("Delete permanently")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {addPassColorOpen
        ? createPortal(
            <div
              className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
              role="dialog"
              aria-modal="true"
              aria-labelledby="add-cab-pass-color-title"
              onClick={() => {
                setAddPassColorOpen(false);
                setPendingPassColor(null);
              }}
            >
              <div
                className={cn(
                  "relative w-full max-w-md border shadow-xl",
                  "rounded-t-2xl sm:rounded-xl",
                  "max-h-[min(90dvh,90vh)] overflow-y-auto overscroll-contain",
                  "p-4 pb-[max(1rem,env(safe-area-inset-bottom,0px))] sm:p-4",
                  isDarkMode ? "border-slate-600 bg-slate-900" : "border-slate-200 bg-white"
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <GbrModalBackdrop />
                <div
                  className={cn(
                    "relative z-10 mx-auto mb-3 h-1 w-10 rounded-full sm:hidden",
                    isDarkMode ? "bg-slate-600" : "bg-slate-300"
                  )}
                  aria-hidden
                />
                <h3
                  id="add-cab-pass-color-title"
                  className={cn("relative z-10 text-base sm:text-sm font-bold", isDarkMode ? "text-slate-100" : "text-slate-900")}
                >
                  {translateUi("Which cab pass do you need to issue?")}
                </h3>
                <p className={cn("relative z-10 mt-1 text-xs", isDarkMode ? "text-slate-400" : "text-slate-600")}>
                  {translateUi("Choose the pass colour before continuing. Nothing is created until you confirm.")}
                </p>
                <div className="relative z-10 mt-3 grid grid-cols-1 gap-2" role="radiogroup" aria-label={translateUi("Cab pass colour")}>
                  {CAB_PASS_COLOR_OPTIONS.map((opt) => {
                    const selected = pendingPassColor === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => setPendingPassColor(opt.value)}
                        className={cn(
                          "flex w-full items-start gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors",
                          selected
                            ? isDarkMode
                              ? "border-indigo-400 bg-indigo-500/20 ring-2 ring-indigo-400/50"
                              : "border-indigo-400 bg-indigo-50 ring-2 ring-indigo-300/60"
                            : isDarkMode
                              ? "border-slate-600 bg-slate-800/50 hover:bg-slate-800"
                              : "border-slate-200 bg-slate-50 hover:bg-slate-100"
                        )}
                      >
                        <span className={cn("mt-0.5 h-4 w-4 shrink-0 rounded-full border border-black/10", opt.swatch)} />
                        <span className="min-w-0 flex-1">
                          <span className={cn("block text-sm font-semibold", isDarkMode ? "text-slate-100" : "text-slate-900")}>
                            {translateUi(opt.label)}
                          </span>
                          <span className={cn("block text-xs mt-0.5", isDarkMode ? "text-slate-400" : "text-slate-600")}>
                            {translateUi(opt.description || passSubtitleForColor(opt.value))}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
                <div className="relative z-10 mt-4 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setAddPassColorOpen(false);
                      setPendingPassColor(null);
                    }}
                    className={cn(
                      "w-full sm:w-auto rounded-lg border px-3 py-2.5 sm:py-1.5 text-sm sm:text-xs font-semibold",
                      isDarkMode
                        ? "border-slate-500 text-slate-200 hover:bg-slate-800"
                        : "border-slate-300 text-slate-700 hover:bg-slate-50"
                    )}
                  >
                    {" "}
                    {translateUi("Cancel")}{" "}
                  </button>
                  <button
                    type="button"
                    disabled={!pendingPassColor}
                    onClick={() => {
                      if (!pendingPassColor) return;
                      const id = addPassWithColor(pendingPassColor);
                      // Kick scroll from the click handler too — don't rely only on the effect.
                      if (!shouldUseCabPassCompactScroll(isMobileOrTablet)) return;
                      const run = () => {
                        mobileFieldFocusCleanupRef.current?.();
                        mobileFieldFocusCleanupRef.current = focusCabPassValidFromField(id);
                      };
                      [0, 50, 120, 250, 450, 700, 1100, 1600, 2400, 3200].forEach((ms) =>
                        window.setTimeout(run, ms)
                      );
                    }}
                    className={cn(
                      "w-full sm:w-auto rounded-lg border px-3 py-2.5 sm:py-1.5 text-sm sm:text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed",
                      isDarkMode
                        ? "border-indigo-500/40 bg-indigo-500/15 text-indigo-200 hover:bg-indigo-500/25"
                        : "border-indigo-200 bg-indigo-50 text-indigo-900 hover:bg-indigo-100"
                    )}
                  >
                    {" "}
                    {translateUi("Continue")}{" "}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      {commitsCabPassesToServer && renewingPassId ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className={cn("w-full max-w-sm rounded-xl border p-4 shadow-xl", isDarkMode ? "border-slate-600 bg-slate-900" : "border-slate-200 bg-white")}>
            <h3 className={cn("text-sm font-bold", isDarkMode ? "text-slate-100" : "text-slate-900")}>{translateUi("Renew cab pass")}</h3>
            <p className={cn("mt-1 text-xs", isDarkMode ? "text-slate-400" : "text-slate-600")}>
              {translateUi("Set new valid dates and refresh the verification QR token.")}
            </p>
            <div className="mt-3 grid grid-cols-1 gap-3">
              <div>
                <label className={labelClass}>{translateUi("Valid from")}</label>
                <input type="date" value={renewFrom} onChange={(e) => setRenewFrom(e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>{translateUi("Valid to")}</label>
                <input type="date" value={renewTo} onChange={(e) => setRenewTo(e.target.value)} className={inputClass} />
              </div>
            </div>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setRenewingPassId(null)}
                className={cn(
                  "rounded-lg border px-3 py-1.5 text-xs font-semibold",
                  isDarkMode ? "border-slate-500 text-slate-200 hover:bg-slate-800" : "border-slate-300 text-slate-700 hover:bg-slate-50"
                )}
              > {translateUi("Cancel")} </button>
              <button
                type="button"
                onClick={() => {
                  renewPass(renewingPassId, renewFrom, renewTo);
                  setRenewingPassId(null);
                }}
                className={cn(
                  "rounded-lg border px-3 py-1.5 text-xs font-semibold",
                  isDarkMode
                    ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25"
                    : "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                )}
              >
                {translateUi("Apply renewal")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
